<template>
  <div class="flex flex-col min-h-screen">
    <Header />
    <FlashMessage v-if="this.messages.text != ''" :message="this.messages" />

    <div class="flex-1 py-7 bg-slate-100">
      <div class="mx-auto max-w-[1200px] px-4 sm:px-0">
        <div
          class="flex flex-col gap-5 bg-white shadow-xl border border-gray-100 rounded-2xl p-6"
        >
          <div class="  ">
            <!-- Header -->
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-xl font-extrabold text-gray-800 tracking-tight">
                  Feedback Analytics
                </h2>
                <p class="text-sm text-gray-500 font-medium">
                  Average ratings per category (out of 5.0)
                </p>
              </div>
            </div>

            <!-- Grid of Donut Charts -->
            <div
              class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mt-4"
            >
              <div
                v-if="loadingCharts"
                v-for="i in 7"
                :key="'skeleton-' + i"
                class="flex flex-col items-center rounded-2xl border-2 border-gray-100 bg-gray-50 pb-3 pt-1 animate-pulse"
              >
                <div class="relative w-full aspect-square max-w-[110px]">
                  <div class="w-full h-full rounded-full bg-gray-200"></div>
                </div>
                <div class="h-3 w-16 bg-gray-200 rounded mt-1"></div>
              </div>
              <div
                v-for="(item, index) in seriesData"
                :key="index"
                @click="item.label !== 'Over ALL' && selectChart(item.label)"
                :class="[
                  'flex relative flex-col items-center rounded-2xl border-2 transition-all duration-300 pb-3 pt-1',
                  'border hover:shadow-xl hover:-translate-y-1',
                  item.label !== 'Over ALL' ? 'cursor-pointer' : '',
                  item.label === 'Over ALL'
                    ? 'bg-blue-50 border-blue-100'
                    : selectedChartLabel === item.label
                    ? ' z-10 border-blue-500 shadow-xl shadow-blue-500/25 scale-[1.03] ring-2 ring-blue-500/20 bg-white'
                    : 'bg-white  hover:bg-gray-50 border-slate-200/60 hover:border-blue-300 hover:bg-blue-50/30 cursor-pointer',
                  'transition-all duration-300 ease-out',
                ]"
              >
                <!-- <div
                  v-if="
                    item.label === selectedChartLabel &&
                    item.label !== 'Over ALL'
                  "
                  class="absolute -top-2 -right-2 bg-blue-500 rounded-full p-1.5 shadow-lg shadow-blue-500/30 animate-pulse"
                >
                  <svg
                    class="w-3 h-3 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>

                <div
                  v-if="item.label === 'Over ALL'"
                  class="absolute -top-2 -right-2 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full px-2 py-0.5 shadow-lg shadow-amber-500/30"
                >
                  <span class="text-[8px] font-bold text-white uppercase"
                    >Top</span
                  >
                </div> -->

                <div
                  class="relative w-full aspect-square max-w-[120px] mx-auto"
                >
                  <ApexChart
                    type="radialBar"
                    height="100%"
                    width="100%"
                    :options="
                      getChartOptions(
                        item.label,
                        item.label === 'Over ALL' ||
                          item.label === selectedChartLabel,
                      )
                    "
                    :series="[item.percentage]"
                  />

                  <!-- Center Overlay -->
                  <div
                    class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
                  >
                    <span
                      class="text-2xl font-bold text-slate-800 leading-none tracking-tight"
                    >
                      {{ item.rating }}
                    </span>
                    <span
                      class="text-[7px] font-semibold uppercase tracking-widest text-slate-400 mt-0.5"
                    >
                      Stars
                    </span>
                  </div>
                </div>
                <!-- Label -->
                <div class="text-center mt-2">
                  <h3 class="text-xs font-bold text-slate-700 tracking-wide">
                    {{ item.label }}
                  </h3>

                  <!-- Star Rating Bar -->
                  <div class="flex items-center gap-0.5 mt-1">
                    <template v-for="i in 5" :key="i">
                      <!-- Full Star -->
                      <svg
                        v-if="item.rating >= i"
                        class="w-3 h-3 text-amber-400 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                        />
                      </svg>

                      <!-- Half Star -->
                      <div
                        v-else-if="item.rating >= i - 0.5"
                        class="relative w-3 h-3"
                      >
                        <svg
                          class="absolute inset-0 w-3 h-3 text-slate-200 fill-current"
                          viewBox="0 0 20 20"
                        >
                          <path
                            d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                          />
                        </svg>

                        <div class="absolute inset-0 overflow-hidden w-1/2">
                          <svg
                            class="w-3 h-3 text-amber-400 fill-current"
                            viewBox="0 0 20 20"
                          >
                            <path
                              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                            />
                          </svg>
                        </div>
                      </div>

                      <!-- Empty Star -->
                      <svg
                        v-else
                        class="w-3 h-3 text-slate-200 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                        />
                      </svg>
                    </template>
                  </div>
                  <!-- Percentage -->
                  <div class="text-[9px] font-medium text-slate-400 mt-0.5">
                    {{ Math.round(item.percentage) }}%
                  </div>
                </div>
                <!-- <h3
                  class="text-[12px] text-center px-1 uppercase font-bold mt-1"
                  :class="
                    item.label === 'Over ALL'
                      ? 'text-gray-500'
                      : 'text-gray-500'
                  "
                >
                  {{ item.label }}
                </h3> -->
              </div>
            </div>
          </div>

          <div
            v-if="response_message"
            class="lg:col-span-3 bg-blues rounded-2xl shadow-lg shadow-blue-200 p-4 flex flex-col sm:flex-row items-center justify-between gap-4"
          >
            <div class="flex items-center gap-3">
              <div class="bg-white/20 p-2 rounded-xl backdrop-blur-md">
                <svg
                  class="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2.5"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p class="text-sm font-bold text-white">{{ response_message }}</p>
            </div>
            <router-link
              :to="{ name: 'DownloadHistory' }"
              class="bg-white text-textblues px-5 py-2 rounded-xl text-xs hover:bg-blue-50 transition-colors"
            >
              View History
            </router-link>
          </div>

          <!-- ASSIGNMENTS TABLE -->
          <div class="w-full space-y-6" v-if="selectedId === 'Assignments'">
            <div
              class="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between"
            >
              <div class="flex-1 space-y-4 w-full">
                <div class="flex flex-col gap-1 mb-2">
                  <h1 class="text-sm font-semibold text-slate-800">
                    Assignment
                  </h1>
                  <p class="text-xs text-gray-500 font-medium">
                    Total Count :
                    <span class="text-xs font-bold text-blue-600">{{
                      assignmentTotalCount
                    }}</span>
                    <span
                      v-if="selectedAssignments.length > 0"
                      class="ml-3 pl-3 border-l border-gray-200 text-xs font-medium text-indigo-600"
                    >
                      Selected: {{ selectedAssignments.length }}
                    </span>
                  </p>
                </div>

                <div class="flex flex-wrap items-center gap-3">
                  <div class="relative flex-grow max-w-md">
                    <span
                      class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"
                    >
                      <img
                        src="@/assets/search_icon1.svg"
                        class="h-4 w-4 opacity-40"
                        alt="Search"
                      />
                    </span>
                    <input
                      type="text"
                      v-model="assignmentSearch"
                      @input="getAssignment(1)"
                      placeholder="Search assignment title..."
                      class="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-300 rounded-2xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </div>

                  <div class="w-48">
                    <select
                      v-model="assignmentSelectedCourse"
                      @change="
                        fetchAllAssignment();
                        getAssignment(1);
                        fetchAssignment(assignmentSelectedCourse);
                        fetchChartAssignment();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">All Courses</option>
                      <option
                        v-for="course in courses"
                        :key="course.id"
                        :value="course.id"
                      >
                        {{ course.course_title }}
                      </option>
                    </select>
                  </div>

                  <div class="w-48">
                    <select
                      v-model="assignmentSelectedItem"
                      @change="
                        fetchAllAssignment();
                        getAssignment(1);
                        fetchChartAssignment();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">Select Assignment</option>
                      <option
                        v-for="ass in Assignments"
                        :key="ass.id"
                        :value="ass.id"
                      >
                        {{ ass.title }}
                      </option>
                    </select>
                  </div>
                  <div class="w-28">
                    <select
                      v-model="selectAssignRating"
                      @change="
                        getAssignment(1);
                        fetchAllAssignment();
                      "
                      class="h-10 px-3 py-2 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-700 w-full font-medium focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                    >
                      <option value="">All Ratings</option>
                      <option
                        value="5"
                        class="bg-white text-slate-800 font-normal"
                      >
                        5 ⭐
                      </option>
                      <option
                        value="4"
                        class="bg-white text-slate-800 font-normal"
                      >
                        4 ⭐
                      </option>
                      <option
                        value="3"
                        class="bg-white text-slate-800 font-normal"
                      >
                        3 ⭐
                      </option>
                      <option
                        value="2"
                        class="bg-white text-slate-800 font-normal"
                      >
                        2 ⭐
                      </option>
                      <option
                        value="1"
                        class="bg-white text-slate-800 font-normal"
                      >
                        1 ⭐
                      </option>
                      <option
                        value="0"
                        class="bg-white text-slate-800 font-normal"
                      >
                        0 ★
                      </option>
                    </select>
                  </div>

                  <button
                    @click="clearAssignmentFilters"
                    title="Clear Filters"
                    class="h-10 flex items-center justify-center p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>

                  <button
                    v-if="feedback_list.length > 0"
                    @click="feedget(selectedId)"
                    class="h-10 flex items-center justify-center gap-2 bg-blues hover:bg-hover text-white rounded-xl py-1.5 px-6 text-sm font-semibold whitespace-nowrap border border-transparent"
                  >
                    <img
                      src="@/assets/newd/download1.png"
                      class="h-4 w-4 brightness-0 invert"
                      alt="Download"
                    />
                    <span>Download</span>
                  </button>
                </div>
              </div>
              <div class="flex-shrink-0 w-[140px]">
                <div
                  v-if="assignmentSelectedItem"
                  class="flex flex-col items-center bg-slate-50 p-3 rounded-3xl border border-slate-100 min-w-[120px] shadow-sm"
                >
                  <div class="relative w-28 h-24">
                    <ApexChart
                      type="radialBar"
                      :options="getChartAssignOptions('Assignments')"
                      :series="[assignmentChartData.percentage]"
                      height="100%"
                      width="100%"
                    />
                    <div
                      class="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <span
                        class="text-lg font-black text-slate-800 leading-none"
                      >
                        {{
                          assignmentChartData.rating
                            ? Number(assignmentChartData.rating).toFixed(1)
                            : "0.0"
                        }}
                      </span>
                    </div>
                  </div>
                  <div class="space-y-1">
                    <div class="flex gap-0.5">
                      <svg
                        v-for="i in 5"
                        :key="i"
                        :class="
                          i <= assignmentChartData.rating
                            ? 'text-amber-400'
                            : 'text-slate-200'
                        "
                        class="w-3 h-3 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                        />
                      </svg>
                    </div>
                  </div>
                  <p
                    class="text-[11px] text-gray-500 font-medium break-words max-w-32 mt-1"
                  >
                    Filter categories by their average rating (out of 5.0).
                  </p>
                </div>
              </div>
            </div>
            <div class="pb-20">
              <div
                class="w-full rounded-2xl shadow-sm border border-slate-200 overflow-x-auto"
              >
                <table class="min-w-full divide-y divide-gray-200 text-xs">
                  <thead class="bg-gray-50">
                    <tr class="divide-x divide-gray-200 whitespace-nowrap">
                      <th
                        class="py-2 px-3 font-semibold text-textblues text-left w-12"
                      >
                        <input
                          type="checkbox"
                          v-model="selectAllAssignment"
                          @change="toggleSelectAll('Assignments')"
                          class="w-4 h-4 rounded text-blue-600 border-gray-400"
                        />
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left w-12"
                      >
                        S.No
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left min-w-[180px]"
                      >
                        Student Info
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]"
                      >
                        Course Title
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]"
                      >
                        Assignment Title
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left min-w-[220px]"
                      >
                        Feedback
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left w-32"
                      >
                        Average Ratings
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left w-24"
                      >
                        Created At
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    v-if="loading"
                    class="divide-y divide-gray-100 bg-white"
                  >
                    <tr
                      v-for="n in 5"
                      :key="'loader-' + n"
                      class="animate-pulse divide-x divide-gray-50 whitespace-nowrap"
                    >
                      <td class="py-3 px-3">
                        <div class="h-4 w-4 bg-gray-200 rounded"></div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="h-3 w-6 bg-gray-100 rounded"></div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="flex items-center gap-2">
                          <div
                            class="w-7 h-7 rounded-full bg-gray-200 flex-shrink-0"
                          ></div>
                          <div class="flex flex-col gap-1.5 w-full">
                            <div class="h-3 w-28 bg-gray-200 rounded"></div>
                            <div class="h-2.5 w-36 bg-gray-100 rounded"></div>
                          </div>
                        </div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="h-3 w-40 bg-gray-100 rounded"></div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="h-3 w-36 bg-gray-100 rounded"></div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="flex flex-col gap-1 w-full">
                          <div class="h-3 w-44 bg-gray-100 rounded"></div>
                          <div class="h-3 w-28 bg-gray-50 rounded"></div>
                        </div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="flex items-center gap-1.5">
                          <div class="flex gap-0.5">
                            <div
                              v-for="i in 5"
                              :key="i"
                              class="w-3.5 h-3.5 bg-gray-200 rounded-sm"
                            ></div>
                          </div>
                          <div class="h-2.5 w-6 bg-gray-100 rounded"></div>
                        </div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="h-3 w-16 bg-gray-100 rounded"></div>
                      </td>
                    </tr>
                  </tbody>

                  <tbody
                    v-else-if="assignment_list.length > 0"
                    class="divide-y divide-gray-100 bg-white"
                  >
                    <tr
                      v-for="(el, index) in assignment_list"
                      :key="index"
                      class="hover:bg-gray-50 transition-colors divide-x divide-gray-100"
                    >
                      <td
                        class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap"
                      >
                        <input
                          type="checkbox"
                          :value="{
                            Assignfeedback_id: el.id,
                            Assignuser_id: el.user_id?.id,
                          }"
                          v-model="selectedAssignments"
                          class="w-4 h-4 rounded text-blue-600 border-gray-400"
                        />
                      </td>
                      <td
                        class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap"
                      >
                        {{
                          (assignmentCurrentPage - 1) * assignmentPageSize +
                          index +
                          1
                        }}
                      </td>
                      <td class="py-2 px-3 text-left whitespace-nowrap">
                        <div class="flex items-center gap-2">
                          <div
                            class="w-7 h-7 flex-shrink-0 rounded-full bg-indigo-50 flex items-center justify-center text-[10px] font-bold text-indigo-600"
                          >
                            {{ el.user_id?.display_name?.charAt(0) || "?" }}
                          </div>
                          <div class="flex flex-col min-w-0">
                            <span
                              class="text-xs font-semibold text-slate-700 truncate max-w-[150px]"
                            >
                              {{ el.user_id?.display_name || "NA" }}
                            </span>
                            <div
                              class="text-[10px] text-gray-400 flex items-center gap-1 truncate max-w-[150px]"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="10px"
                                viewBox="0 -960 960 960"
                                width="10px"
                                fill="#9CA3AF"
                              >
                                <path
                                  d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z"
                                />
                              </svg>
                              {{ el.user_id?.email || "NA" }}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        class="py-2 px-3 text-left font-medium text-slate-700"
                      >
                        <div
                          class="w-[200px] md:max-w-[180px] break-words line-clamp-3"
                        >
                          {{ el.assignment?.course?.title || "NA" }}
                        </div>
                      </td>
                      <td
                        class="py-2 px-3 text-left font-medium text-slate-700"
                      >
                        <div
                          class="w-[200px] md:max-w-[150px] break-words line-clamp-3"
                        >
                          {{ el.assignment?.title || "NA" }}
                        </div>
                      </td>
                      <td class="py-2 px-3 text-left text-gray-600">
                        <div class="max-w-[200px] break-words">
                          <p
                            :class="
                              isExpanded('Assignments', index)
                                ? ''
                                : 'line-clamp-2'
                            "
                          >
                            {{ el.text || "NA" }}
                          </p>
                          <span
                            v-if="el.text && el.text.length > 80"
                            @click="toggleRead('Assignments', index)"
                            class="text-blue-600 text-xs cursor-pointer hover:underline"
                          >
                            {{
                              isExpanded("Assignments", index)
                                ? "Read Less"
                                : "Read More"
                            }}
                          </span>
                        </div>
                      </td>
                      <td class="py-2 px-3 text-left whitespace-nowrap">
                        <div class="flex items-center gap-1.5">
                          <div class="flex gap-0.5">
                            <div v-for="i in 5" :key="i">
                              <svg
                                :class="[
                                  'w-3.5 h-3.5',
                                  i <= el.rating
                                    ? 'text-yellow-400'
                                    : 'text-gray-200',
                                ]"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                                />
                              </svg>
                            </div>
                          </div>
                          <span class="text-[10px] text-gray-400 font-semibold"
                            >({{ el.rating }})</span
                          >
                        </div>
                      </td>
                      <td
                        class="py-2 px-3 text-left whitespace-nowrap text-gray-500 font-medium"
                      >
                        {{ el.created_at ? formatDate(el.created_at) : "NA" }}
                      </td>
                    </tr>
                  </tbody>

                  <tbody v-else>
                    <tr>
                      <td
                        colspan="8"
                        class="text-center py-8 text-gray-600 font-medium"
                      >
                        No Data Found
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative"
              >
                <div
                  class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal"
                >
                  <span class="flex md:hidden">Rows per page:</span>
                  <select
                    v-model="assignmentPageSize"
                    @change="onAssignmentPageSizeChange"
                    class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option :value="10">10</option>
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                  </select>
                </div>
                <div
                  v-if="assignmentHasLoaded && assignmentTotalPages > 1"
                  class="flex justify-center"
                >
                  <Pagination
                    :key="'assignments-pagination'"
                    :totalPages="assignmentTotalPages"
                    @page-changed="getAssignment"
                  />
                </div>
              </div>
            </div>
            <!-- <div
              v-else
              class="py-20 text-center bg-white rounded-[32px] border border-dashed border-slate-300"
            >
              <div class="inline-flex p-4 bg-slate-50 rounded-full mb-4">
                <svg
                  class="w-8 h-8 text-slate-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 class="text-slate-800 font-bold">No Data Found</h3>
            </div> -->
          </div>

          <!-- ASSESSMENTS TABLE -->
          <div class="w-full space-y-6" v-if="selectedId === 'Assessments'">
            <div
              class="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between"
            >
              <div class="flex-1 space-y-4 w-full">
                <div class="flex flex-col gap-1 mb-2">
                  <h1 class="text-sm font-semibold text-slate-800">
                    Assessments
                  </h1>
                  <p class="text-xs text-gray-500 font-medium">
                    Total Count :
                    <span class="text-xs font-bold text-blue-600">{{
                      assessmentTotalCount
                    }}</span>
                    <span
                      v-if="selectedAssessments.length > 0"
                      class="ml-3 pl-3 border-l border-gray-200 text-xs font-medium text-indigo-600"
                    >
                      Selected: {{ selectedAssessments.length }}
                    </span>
                  </p>
                </div>

                <div class="flex flex-wrap items-center gap-3">
                  <div class="relative flex-grow max-w-md">
                    <span
                      class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"
                    >
                      <img
                        src="@/assets/search_icon1.svg"
                        class="h-4 w-4 opacity-40"
                        alt="Search"
                      />
                    </span>
                    <input
                      type="text"
                      v-model="assessmentSearch"
                      @input="getAssessments(1)"
                      placeholder="Search Assessments title..."
                      class="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-300 rounded-2xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </div>

                  <div class="w-48">
                    <select
                      v-model="assessmentSelectedCourse"
                      @change="
                        fetchAllAssessment();
                        getAssessments(1);
                        fetchAssessments(assessmentSelectedCourse);
                        fetchChartAssessment();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">All Courses</option>
                      <option
                        v-for="course in courses"
                        :key="course.id"
                        :value="course.id"
                      >
                        {{ course.course_title }}
                      </option>
                    </select>
                  </div>

                  <div class="w-48">
                    <select
                      v-model="assessmentSelectedItem"
                      @change="
                        fetchAllAssessment();
                        getAssessments(1);
                        fetchChartAssessment();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">Select Assessments</option>
                      <option
                        v-for="Assessment in Assessments"
                        :key="Assessment.id"
                        :value="Assessment.id"
                      >
                        {{ Assessment.name }}
                      </option>
                    </select>
                  </div>
                  <div class="w-28">
                    <select
                      v-model="selectAssessRating"
                      @change="
                        getAssessments(1);
                        fetchAllAssessment();
                      "
                      class="h-10 px-3 py-2 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-700 w-full font-medium focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                    >
                      <option value="">All Ratings</option>
                      <option
                        value="5"
                        class="bg-white text-slate-800 font-normal"
                      >
                        5 ⭐
                      </option>
                      <option
                        value="4"
                        class="bg-white text-slate-800 font-normal"
                      >
                        4 ⭐
                      </option>
                      <option
                        value="3"
                        class="bg-white text-slate-800 font-normal"
                      >
                        3 ⭐
                      </option>
                      <option
                        value="2"
                        class="bg-white text-slate-800 font-normal"
                      >
                        2 ⭐
                      </option>
                      <option
                        value="1"
                        class="bg-white text-slate-800 font-normal"
                      >
                        1 ⭐
                      </option>
                      <option
                        value="0"
                        class="bg-white text-slate-800 font-normal"
                      >
                        0 ★
                      </option>
                    </select>
                  </div>
                  <button
                    @click="clearAssessmentFilters"
                    title="Clear Filters"
                    class="h-10 flex items-center justify-center p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>

                  <button
                    v-if="feedback_list.length > 0"
                    @click="feedget(selectedId)"
                    class="h-10 flex items-center justify-center gap-2 bg-blues hover:bg-hover text-white rounded-xl py-1.5 px-6 text-sm font-semibold whitespace-nowrap border border-transparent"
                  >
                    <img
                      src="@/assets/newd/download1.png"
                      class="h-4 w-4 brightness-0 invert"
                      alt="Download"
                    />
                    <span>Download</span>
                  </button>
                </div>
              </div>
              <div class="flex-shrink-0 w-[140px]">
                <div
                  v-if="assessmentSelectedItem"
                  class="flex flex-col items-center bg-slate-50 p-3 rounded-3xl border border-slate-100 min-w-[120px] shadow-sm"
                >
                  <div class="relative w-28 h-24">
                    <ApexChart
                      type="radialBar"
                      :options="getChartAssessOptions('Assessments')"
                      :series="[assessmentChartData.percentage]"
                      height="100%"
                      width="100%"
                    />
                    <div
                      class="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <span
                        class="text-lg font-black text-slate-800 leading-none"
                        >{{ assessmentChartData.rating }}</span
                      >
                    </div>
                  </div>
                  <div class="space-y-1">
                    <div class="flex gap-0.5">
                      <svg
                        v-for="i in 5"
                        :key="i"
                        :class="
                          i <= assessmentChartData.rating
                            ? 'text-amber-400'
                            : 'text-slate-200'
                        "
                        class="w-3 h-3 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                        />
                      </svg>
                    </div>
                  </div>
                  <p
                    class="text-[11px] text-gray-500 font-medium break-words max-w-32 mt-1"
                  >
                    Filter categories by their average rating (out of 5.0).
                  </p>
                </div>
              </div>
            </div>

            <div class="pb-20">
              <div
                class="w-full rounded-2xl shadow-sm border border-gray-200 overflow-x-auto"
              >
                <table class="min-w-full divide-y divide-gray-200 text-xs">
                  <thead class="bg-gray-50">
                    <tr class="divide-x divide-gray-200 whitespace-nowrap">
                      <th
                        class="py-2 px-3 font-semibold text-textblues text-left w-12"
                      >
                        <input
                          type="checkbox"
                          v-model="selectAllAssessment"
                          @change="toggleSelectAll('Assessments')"
                          class="w-4 h-4 rounded text-blue-600 border-gray-400"
                        />
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left w-12"
                      >
                        S.No
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left min-w-[180px]"
                      >
                        Student Info
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]"
                      >
                        Course Title
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]"
                      >
                        Assessment Title
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left min-w-[220px]"
                      >
                        Feedback
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left w-32"
                      >
                        Average Ratings
                      </th>
                      <th
                        scope="col"
                        class="py-2 px-3 font-semibold text-textblues text-left w-24"
                      >
                        Created At
                      </th>
                    </tr>
                  </thead>

                  <tbody
                    v-if="loading"
                    class="divide-y divide-gray-100 bg-white"
                  >
                    <tr
                      v-for="n in 5"
                      :key="'loader-' + n"
                      class="animate-pulse divide-x divide-gray-50 whitespace-nowrap"
                    >
                      <td class="py-3 px-3">
                        <div class="h-4 w-4 bg-gray-200 rounded"></div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="h-3 w-6 bg-gray-100 rounded"></div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="flex items-center gap-2">
                          <div
                            class="w-7 h-7 rounded-full bg-gray-200 flex-shrink-0"
                          ></div>
                          <div class="flex flex-col gap-1.5 w-full">
                            <div class="h-3 w-28 bg-gray-200 rounded"></div>
                            <div class="h-2.5 w-36 bg-gray-100 rounded"></div>
                          </div>
                        </div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="h-3 w-40 bg-gray-100 rounded"></div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="h-3 w-36 bg-gray-100 rounded"></div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="flex flex-col gap-1 w-full">
                          <div class="h-3 w-44 bg-gray-100 rounded"></div>
                          <div class="h-3 w-28 bg-gray-50 rounded"></div>
                        </div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="flex items-center gap-1.5">
                          <div class="flex gap-0.5">
                            <div
                              v-for="i in 5"
                              :key="i"
                              class="w-3.5 h-3.5 bg-gray-200 rounded-sm"
                            ></div>
                          </div>
                          <div class="h-2.5 w-6 bg-gray-100 rounded"></div>
                        </div>
                      </td>
                      <td class="py-3 px-3">
                        <div class="h-3 w-16 bg-gray-100 rounded"></div>
                      </td>
                    </tr>
                  </tbody>

                  <tbody
                    v-else-if="assessment_list.length > 0"
                    class="divide-y divide-gray-100 bg-white"
                  >
                    <tr
                      v-for="(el, index) in assessment_list"
                      :key="index"
                      class="hover:bg-gray-50 transition-colors divide-x divide-gray-100"
                    >
                      <td
                        class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap"
                      >
                        <input
                          type="checkbox"
                          :value="{
                            Assessfeedback_id: el.id,
                            Assessuser_id: el.user_id?.id,
                          }"
                          v-model="selectedAssessments"
                          class="w-4 h-4 rounded text-blue-600 border-gray-400"
                        />
                      </td>
                      <td
                        class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap"
                      >
                        {{
                          (assessmentCurrentPage - 1) * assessmentPageSize +
                          index +
                          1
                        }}
                      </td>
                      <td class="py-2 px-3 text-left whitespace-nowrap">
                        <div class="flex items-center gap-2">
                          <div
                            class="w-7 h-7 flex-shrink-0 rounded-full bg-indigo-50 flex items-center justify-center text-[10px] font-bold text-indigo-600"
                          >
                            {{ el.user_id?.display_name?.charAt(0) || "?" }}
                          </div>
                          <div class="flex flex-col min-w-0">
                            <span
                              class="text-xs font-semibold text-slate-700 truncate max-w-[150px]"
                            >
                              {{ el.user_id?.display_name || "NA" }}
                            </span>
                            <div
                              class="text-[10px] text-gray-400 flex items-center gap-1 truncate max-w-[150px]"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                height="10px"
                                viewBox="0 -960 960 960"
                                width="10px"
                                fill="#9CA3AF"
                              >
                                <path
                                  d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z"
                                />
                              </svg>
                              {{ el.user_id?.email || "NA" }}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        class="py-2 px-3 text-left font-medium text-slate-700"
                      >
                        <div
                          class="w-[200px] md:max-w-[180px] break-words line-clamp-3"
                        >
                          {{ el.quize?.course_title || "NA" }}
                        </div>
                      </td>
                      <td
                        class="py-2 px-3 text-left font-medium text-slate-700"
                      >
                        <div
                          class="w-[200px] md:max-w-[150px] break-words line-clamp-3"
                        >
                          {{ el.quize?.name || "NA" }}
                        </div>
                      </td>
                      <td class="py-2 px-3 text-left text-gray-600">
                        <div class="max-w-[200px] break-words">
                          <p
                            :class="
                              isExpanded('Assessments', index)
                                ? ''
                                : 'line-clamp-2'
                            "
                          >
                            {{ el.text || "NA" }}
                          </p>
                          <span
                            v-if="el.text && el.text.length > 80"
                            @click="toggleRead('Assessments', index)"
                            class="text-blue-600 text-xs cursor-pointer hover:underline"
                          >
                            {{
                              isExpanded("Assessments", index)
                                ? "Read Less"
                                : "Read More"
                            }}
                          </span>
                        </div>
                      </td>
                      <td class="py-2 px-3 text-left whitespace-nowrap">
                        <div class="flex items-center gap-1.5">
                          <div class="flex gap-0.5">
                            <div v-for="i in 5" :key="i">
                              <svg
                                :class="[
                                  'w-3.5 h-3.5',
                                  i <= el.rating
                                    ? 'text-yellow-400'
                                    : 'text-gray-200',
                                ]"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                                />
                              </svg>
                            </div>
                          </div>
                          <span class="text-[10px] text-gray-400 font-semibold"
                            >({{ el.rating }})</span
                          >
                        </div>
                      </td>
                      <td
                        class="py-2 px-3 text-left whitespace-nowrap text-gray-500 font-medium"
                      >
                        {{ el.created_at ? formatDate(el.created_at) : "NA" }}
                      </td>
                    </tr>
                  </tbody>

                  <tbody v-else>
                    <tr>
                      <td
                        colspan="8"
                        class="text-center py-8 text-gray-600 font-medium"
                      >
                        No Data Found
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative"
              >
                <div
                  class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal"
                >
                  <span class="flex md:hidden">Rows per page:</span>
                  <select
                    v-model="assessmentPageSize"
                    @change="onAssessmentPageSizeChange"
                    class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option :value="10">10</option>
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                    <option :value="100">100</option>
                  </select>
                </div>
                <div
                  v-if="assessmentHasLoaded && assessmentTotalPages > 1"
                  class="flex justify-center"
                >
                  <Pagination
                    :key="'assessments-pagination'"
                    :totalPages="assessmentTotalPages"
                    @page-changed="getAssessments"
                  />
                </div>
              </div>
            </div>
            <!-- <div
              v-else
              class="py-20 text-center bg-white rounded-[32px] border border-dashed border-slate-300"
            >
              <div class="inline-flex p-4 bg-slate-50 rounded-full mb-4">
                <svg
                  class="w-8 h-8 text-slate-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 class="text-slate-800 font-bold">No Data Found</h3>
            </div> -->
          </div>

          <!-- LESSON TABLE -->
          <div class="w-full space-y-6" v-if="selectedId === 'Unit'">
            <div
              class="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between"
            >
              <div class="flex-1 space-y-4 w-full">
                <div class="flex flex-col gap-1 mb-2">
                  <h1 class="text-sm font-semibold text-slate-800">Unit</h1>
                  <p class="text-xs text-gray-500 font-medium">
                    Total Count :
                    <span class="text-xs font-bold text-blue-600">{{
                      lessonTotalCount
                    }}</span>
                    <span
                      v-if="selectedUnit.length > 0"
                      class="ml-3 pl-3 border-l border-gray-200 text-xs font-medium text-indigo-600"
                    >
                      Selected: {{ selectedUnit.length }}
                    </span>
                  </p>
                </div>

                <div class="flex flex-wrap items-center gap-3">
                  <div class="relative flex-grow max-w-md">
                    <span
                      class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"
                    >
                      <img
                        src="@/assets/search_icon1.svg"
                        class="h-4 w-4 opacity-40"
                        alt="Search"
                      />
                    </span>
                    <input
                      type="text"
                      v-model="lessonSearch"
                      @input="getLesson(1)"
                      placeholder="Search Unit title..."
                      class="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-300 rounded-2xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </div>

                  <div class="w-48">
                    <select
                      v-model="lessonSelectedCourse"
                      @change="
                        fetchAllUnit();
                        getLesson(1);
                        fetchLesson(lessonSelectedCourse);
                        fetchChartLesson();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">All Courses</option>
                      <option
                        v-for="course in courses"
                        :key="course.id"
                        :value="course.id"
                      >
                        {{ course.course_title }}
                      </option>
                    </select>
                  </div>

                  <div class="w-48">
                    <select
                      v-model="lessonSelectedItem"
                      @change="
                        fetchAllUnit();
                        getLesson(1);
                        fetchChartLesson();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">Select Unit</option>
                      <option
                        v-for="Lesson in Lessons"
                        :key="Lesson.id"
                        :value="Lesson.id"
                      >
                        {{
                          Lesson.title.length > 40
                            ? Lesson.title.slice(0, 40) + "…"
                            : Lesson.title
                        }}
                      </option>
                    </select>
                  </div>
                  <div class="w-28">
                    <select
                      v-model="selectUnitRating"
                      @change="
                        getLesson(1);
                        fetchAllUnit();
                      "
                      class="h-10 px-3 py-2 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-700 w-full font-medium focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                    >
                      <option value="">All Ratings</option>
                      <option
                        value="5"
                        class="bg-white text-slate-800 font-normal"
                      >
                        5 ⭐
                      </option>
                      <option
                        value="4"
                        class="bg-white text-slate-800 font-normal"
                      >
                        4 ⭐
                      </option>
                      <option
                        value="3"
                        class="bg-white text-slate-800 font-normal"
                      >
                        3 ⭐
                      </option>
                      <option
                        value="2"
                        class="bg-white text-slate-800 font-normal"
                      >
                        2 ⭐
                      </option>
                      <option
                        value="1"
                        class="bg-white text-slate-800 font-normal"
                      >
                        1 ⭐
                      </option>
                      <option
                        value="0"
                        class="bg-white text-slate-800 font-normal"
                      >
                        0 ★
                      </option>
                    </select>
                  </div>
                  <button
                    @click="clearLessonFilters"
                    title="Clear Filters"
                    class="h-10 flex items-center justify-center p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>

                  <button
                    v-if="feedback_list.length > 0"
                    @click="feedget(selectedId)"
                    class="h-10 flex items-center justify-center gap-2 bg-blues hover:bg-hover text-white rounded-xl py-1.5 px-6 text-sm font-semibold whitespace-nowrap border border-transparent"
                  >
                    <img
                      src="@/assets/newd/download1.png"
                      class="h-4 w-4 brightness-0 invert"
                      alt="Download"
                    />
                    <span>Download</span>
                  </button>
                </div>
              </div>
              <div class="flex-shrink-0 w-[140px]">
                <div
                  v-if="lessonSelectedItem"
                  class="flex flex-col items-center bg-slate-50 p-3 rounded-3xl border border-slate-100 min-w-[120px] shadow-sm"
                >
                  <div class="relative w-28 h-24">
                    <ApexChart
                      type="radialBar"
                      :options="getChartLessonOptions('Unit')"
                      :series="[lessonChartData.percentage]"
                      height="100%"
                      width="100%"
                    />
                    <div
                      class="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <span
                        class="text-lg font-black text-slate-800 leading-none"
                        >{{ lessonChartData.rating }}</span
                      >
                    </div>
                  </div>
                  <div class="space-y-1">
                    <div class="flex gap-0.5">
                      <svg
                        v-for="i in 5"
                        :key="i"
                        :class="
                          i <= lessonChartData.rating
                            ? 'text-amber-400'
                            : 'text-slate-200'
                        "
                        class="w-3 h-3 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                        />
                      </svg>
                    </div>
                  </div>
                  <p
                    class="text-[11px] text-gray-500 font-medium break-words max-w-32 mt-1"
                  >
                    Filter categories by their average rating (out of 5.0).
                  </p>
                </div>
              </div>
            </div>

           <div class="pb-20">
  <div class="w-full rounded-2xl shadow-sm border border-gray-200 overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 text-xs">
      <thead class="bg-gray-50">
        <tr class="divide-x divide-gray-200 whitespace-nowrap">
          <th class="py-2 px-3 font-semibold text-textblues text-left w-12">
            <input
              type="checkbox"
              v-model="selectAllUnit"
              @change="toggleSelectAll('Unit')"
              class="w-4 h-4 rounded text-blue-600 border-gray-400"
            />
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-12">
            S.No
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[180px]">
            Student Info
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]">
            Course Title
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]">
            Unit Title
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[220px]">
            Feedback
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-32">
            Average Ratings
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-24">
            Created At
          </th>
        </tr>
      </thead>

      <tbody v-if="loading" class="divide-y divide-gray-100 bg-white">
        <tr v-for="n in 5" :key="'loader-' + n" class="animate-pulse divide-x divide-gray-50 whitespace-nowrap">
          <td class="py-3 px-3"><div class="h-4 w-4 bg-gray-200 rounded"></div></td>
          <td class="py-3 px-3"><div class="h-3 w-6 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 rounded-full bg-gray-200 flex-shrink-0"></div>
              <div class="flex flex-col gap-1.5 w-full">
                <div class="h-3 w-28 bg-gray-200 rounded"></div>
                <div class="h-2.5 w-36 bg-gray-100 rounded"></div>
              </div>
            </div>
          </td>
          <td class="py-3 px-3"><div class="h-3 w-40 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3"><div class="h-3 w-36 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3">
            <div class="flex flex-col gap-1 w-full">
              <div class="h-3 w-44 bg-gray-100 rounded"></div>
              <div class="h-3 w-28 bg-gray-50 rounded"></div>
            </div>
          </td>
          <td class="py-3 px-3">
            <div class="flex items-center gap-1.5">
              <div class="flex gap-0.5">
                <div v-for="i in 5" :key="i" class="w-3.5 h-3.5 bg-gray-200 rounded-sm"></div>
              </div>
              <div class="h-2.5 w-6 bg-gray-100 rounded"></div>
            </div>
          </td>
          <td class="py-3 px-3"><div class="h-3 w-16 bg-gray-100 rounded"></div></td>
        </tr>
      </tbody>

      <tbody v-else-if="lesson_list.length > 0" class="divide-y divide-gray-100 bg-white">
        <tr
          v-for="(el, index) in lesson_list"
          :key="index"
          class="hover:bg-gray-50 transition-colors divide-x divide-gray-100"
        >
          <td class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap">
            <input
              type="checkbox"
              :value="{ Unitfeedback_id: el.id, Unituser_id: el.user_id?.id }"
              v-model="selectedUnit"
              class="w-4 h-4 rounded text-blue-600 border-gray-400"
            />
          </td>
          <td class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap">
            {{ (lessonCurrentPage - 1) * lessonPageSize + index + 1 }}
          </td>
          <td class="py-2 px-3 text-left whitespace-nowrap">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 flex-shrink-0 rounded-full bg-indigo-50 flex items-center justify-center text-[10px] font-bold text-indigo-600">
                {{ el.user_id?.display_name?.charAt(0) || "?" }}
              </div>
              <div class="flex flex-col min-w-0">
                <span class="text-xs font-semibold text-slate-700 truncate max-w-[150px]">
                  {{ el.user_id?.display_name || "NA" }}
                </span>
                <div class="text-[10px] text-gray-400 flex items-center gap-1 truncate max-w-[150px]">
                  <svg xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="0 -960 960 960" width="10px" fill="#9CA3AF">
                    <path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z"/>
                  </svg>
                  {{ el.user_id?.email || "NA" }}
                </div>
              </div>
            </div>
          </td>
          <td class="py-2 px-3 text-left font-medium text-slate-700">
            <div class="w-[200px] md:max-w-[180px] break-words line-clamp-3">
              {{ el.lesson?.course_title || "NA" }}
            </div>
          </td>
          <td class="py-2 px-3 text-left font-medium text-slate-700">
            <div class="w-[200px] md:max-w-[150px] break-words line-clamp-3">
              {{ el.lesson?.title || "NA" }}
            </div>
          </td>
          <td class="py-2 px-3 text-left text-gray-600">
            <div class="max-w-[200px] break-words">
              <p :class="isExpanded('Unit', index) ? '' : 'line-clamp-2'">
                {{ el.text || "NA" }}
              </p>
              <span
                v-if="el.text && el.text.length > 80"
                @click="toggleRead('Unit', index)"
                class="text-blue-600 text-xs cursor-pointer hover:underline"
              >
                {{ isExpanded("Unit", index) ? "Read Less" : "Read More" }}
              </span>
            </div>
          </td>
          <td class="py-2 px-3 text-left whitespace-nowrap">
            <div class="flex items-center gap-1.5">
              <div class="flex gap-0.5">
                <div v-for="i in 5" :key="i">
                  <svg
                    :class="['w-3.5 h-3.5', i <= el.rating ? 'text-yellow-400' : 'text-gray-200']"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"/>
                  </svg>
                </div>
              </div>
              <span class="text-[10px] text-gray-400 font-semibold">({{ el.rating }})</span>
            </div>
          </td>
          <td class="py-2 px-3 text-left whitespace-nowrap text-gray-500 font-medium">
            {{ el.created_at ? formatDate(el.created_at) : "NA" }}
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="8" class="text-center py-8 text-gray-600 font-medium">
            No Data Found
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative">
    <div class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal">
      <span class="flex md:hidden">Rows per page:</span>
      <select
        v-model="lessonPageSize"
        @change="onLessonPageSizeChange"
        class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>
    <div v-if="lessonHasLoaded && lessonTotalPages > 1" class="flex justify-center">
      <Pagination
        :key="'lesson-pagination'"
        :totalPages="lessonTotalPages"
        @page-changed="getLesson"
      />
    </div>
  </div>
</div>
            <!-- <div
              v-else
              class="py-20 text-center bg-white rounded-[32px] border border-dashed border-slate-300"
            >
              <div class="inline-flex p-4 bg-slate-50 rounded-full mb-4">
                <svg
                  class="w-8 h-8 text-slate-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 class="text-slate-800 font-bold">No Data Found</h3>
            </div> -->
          </div>

          <!-- SESSION TABLE (maintaining same structure) -->
          <div class="w-full space-y-6" v-if="selectedId === 'Module'">
            <div
              class="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between"
            >
              <!-- Left: Title + Search + Filters -->
              <div class="flex-1 space-y-4 w-full">
                <!-- Title + Total Count -->
                <div class="flex flex-col gap-1 mb-2">
                  <h1 class="text-sm font-semibold text-slate-800">Module</h1>
                  <p class="text-xs text-gray-500 font-medium">
                    Total Count :
                    <span class="text-xs font-bold text-blue-600">{{
                      SessionTotalCount
                    }}</span>
                    <span
                      v-if="selectedModule.length > 0"
                      class="ml-3 pl-3 border-l border-gray-200 text-xs font-medium text-indigo-600"
                    >
                      Selected: {{ selectedModule.length }}
                    </span>
                  </p>
                </div>

                <!-- Search + Filters -->
                <div class="flex flex-wrap items-center gap-3">
                  <!-- Search Input -->
                  <div class="relative flex-grow max-w-md">
                    <span
                      class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"
                    >
                      <img
                        src="@/assets/search_icon1.svg"
                        class="h-4 w-4 opacity-40"
                        alt="Search"
                      />
                    </span>
                    <input
                      type="text"
                      v-model="SessionSearch"
                      @input="getSession(1)"
                      placeholder="Search Module title..."
                      class="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-300 rounded-2xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </div>

                  <!-- Course Dropdown -->
                  <div class="w-48">
                    <select
                      v-model="SessionSelectedCourse"
                      @change="
                        fetchAllModule();
                        getSession(1);
                        fetchSession(SessionSelectedCourse);
                        fetchChartSession();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">All Courses</option>
                      <option
                        v-for="course in courses"
                        :key="course.id"
                        :value="course.id"
                      >
                        {{ course.course_title }}
                      </option>
                    </select>
                  </div>

                  <!-- Assignment Dropdown -->
                  <div class="w-48">
                    <select
                      v-model="SessionSelectedItem"
                      @change="
                        fetchAllModule();
                        getSession(1);
                        fetchChartSession();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">Select Module</option>
                      <option
                        v-for="Session in Sessions"
                        :key="Session.id"
                        :value="Session.id"
                      >
                        {{
                          Session.title.length > 40
                            ? Session.title.slice(0, 40) + "…"
                            : Session.title
                        }}
                      </option>
                    </select>
                  </div>
                  <div class="w-28">
                    <select
                      v-model="selectModuleRating"
                      @change="
                        getSession(1);
                        fetchAllModule();
                      "
                      class="h-10 px-3 py-2 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-700 w-full font-medium focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                    >
                      <option value="">All Ratings</option>
                      <option
                        value="5"
                        class="bg-white text-slate-800 font-normal"
                      >
                        5 ⭐
                      </option>
                      <option
                        value="4"
                        class="bg-white text-slate-800 font-normal"
                      >
                        4 ⭐
                      </option>
                      <option
                        value="3"
                        class="bg-white text-slate-800 font-normal"
                      >
                        3 ⭐
                      </option>
                      <option
                        value="2"
                        class="bg-white text-slate-800 font-normal"
                      >
                        2 ⭐
                      </option>
                      <option
                        value="1"
                        class="bg-white text-slate-800 font-normal"
                      >
                        1 ⭐
                      </option>
                      <option
                        value="0"
                        class="bg-white text-slate-800 font-normal"
                      >
                        0 ★
                      </option>
                    </select>
                  </div>

                  <!-- Clear Button -->
                  <button
                    @click="clearSessionFilters"
                    title="Clear Filters"
                    class="h-10 flex items-center justify-center p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>

                  <!-- Download Button -->
                  <button
                    v-if="feedback_list.length > 0"
                    @click="feedget(selectedId)"
                    class="h-10 flex items-center justify-center gap-2 bg-blues hover:bg-hover text-white rounded-xl py-1.5 px-6 text-sm font-semibold whitespace-nowrap border border-transparent"
                  >
                    <img
                      src="@/assets/newd/download1.png"
                      class="h-4 w-4 brightness-0 invert"
                      alt="Download"
                    />
                    <span>Download</span>
                  </button>
                </div>
              </div>

              <!-- Right: Radial Chart Card -->
              <div class="flex-shrink-0 w-[140px]">
                <div
                  v-if="SessionSelectedItem"
                  class="flex flex-col items-center bg-slate-50 p-3 rounded-3xl border border-slate-100 min-w-[120px] shadow-sm"
                >
                  <div class="relative w-28 h-24">
                    <ApexChart
                      type="radialBar"
                      :options="getChartSessionOptions('Module')"
                      :series="[SessionChartData.percentage]"
                      height="100%"
                      width="100%"
                    />
                    <!-- Center overlay -->
                    <div
                      class="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <span
                        class="text-lg font-black text-slate-800 leading-none"
                      >
                        {{ SessionChartData.rating }}
                      </span>
                    </div>
                  </div>
                  <div class="space-y-1">
                    <div class="flex gap-0.5">
                      <svg
                        v-for="i in 5"
                        :key="i"
                        :class="
                          i <= SessionChartData.rating
                            ? 'text-amber-400'
                            : 'text-slate-200'
                        "
                        class="w-3 h-3 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                        />
                      </svg>
                    </div>
                  </div>
                  <p
                    class="text-[11px] text-gray-500 font-medium break-words max-w-32 mt-1"
                  >
                    Filter categories by their average rating (out of 5.0).
                  </p>
                </div>
              </div>
            </div>
           <div class="pb-20">
  <div class="w-full rounded-2xl shadow-sm border border-slate-200 overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 text-xs">
      <thead class="bg-gray-50">
        <tr class="divide-x divide-gray-200 whitespace-nowrap">
          <th class="py-2 px-3 font-semibold text-textblues text-left w-12">
            <input
              type="checkbox"
              v-model="selectAllModule"
              @change="toggleSelectAll('Module')"
              class="w-4 h-4 rounded text-blue-600 border-gray-400"
            />
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-12">
            S.No
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[180px]">
            Student Info
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]">
            Course Title
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]">
            Module Title
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[220px]">
            Feedback
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-32">
            Average Ratings
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-24">
            Created At
          </th>
        </tr>
      </thead>

      <tbody v-if="loading" class="divide-y divide-gray-100 bg-white">
        <tr v-for="n in 5" :key="'loader-' + n" class="animate-pulse divide-x divide-gray-50 whitespace-nowrap">
          <td class="py-3 px-3"><div class="h-4 w-4 bg-gray-200 rounded"></div></td>
          <td class="py-3 px-3"><div class="h-3 w-6 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 rounded-full bg-gray-200 flex-shrink-0"></div>
              <div class="flex flex-col gap-1.5 w-full">
                <div class="h-3 w-28 bg-gray-200 rounded"></div>
                <div class="h-2.5 w-36 bg-gray-100 rounded"></div>
              </div>
            </div>
          </td>
          <td class="py-3 px-3"><div class="h-3 w-40 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3"><div class="h-3 w-36 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3">
            <div class="flex flex-col gap-1 w-full">
              <div class="h-3 w-44 bg-gray-100 rounded"></div>
              <div class="h-3 w-28 bg-gray-50 rounded"></div>
            </div>
          </td>
          <td class="py-3 px-3">
            <div class="flex items-center gap-1.5">
              <div class="flex gap-0.5">
                <div v-for="i in 5" :key="i" class="w-3.5 h-3.5 bg-gray-200 rounded-sm"></div>
              </div>
              <div class="h-2.5 w-6 bg-gray-100 rounded"></div>
            </div>
          </td>
          <td class="py-3 px-3"><div class="h-3 w-16 bg-gray-100 rounded"></div></td>
        </tr>
      </tbody>

      <tbody v-else-if="Session_list.length > 0" class="divide-y divide-gray-100 bg-white">
        <tr
          v-for="(el, index) in Session_list"
          :key="index"
          class="hover:bg-gray-50 transition-colors divide-x divide-gray-100"
        >
          <td class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap">
            <input
              type="checkbox"
              :value="{ Modulefeedback_id: el.id, Moduleuser_id: el.user_id?.id }"
              v-model="selectedModule"
              class="w-4 h-4 rounded text-blue-600 border-gray-400"
            />
          </td>
          <td class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap">
            {{ (SessionCurrentPage - 1) * SessionPageSize + index + 1 }}
          </td>
          <td class="py-2 px-3 text-left whitespace-nowrap">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 flex-shrink-0 rounded-full bg-indigo-50 flex items-center justify-center text-[10px] font-bold text-indigo-600">
                {{ el.user_id?.display_name?.charAt(0) || "?" }}
              </div>

              <div class="flex flex-col min-w-0">
                <span class="text-xs font-semibold text-slate-700 truncate max-w-[150px]">
                  {{ el.user_id?.display_name || "NA" }}
                </span>
                <div class="text-[10px] text-gray-400 flex items-center gap-1 truncate max-w-[150px]">
                  <svg xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="0 -960 960 960" width="10px" fill="#9CA3AF">
                    <path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z"/>
                  </svg>
                  {{ el.user_id?.email || "NA" }}
                </div>
              </div>
            </div>
          </td>

          <td class="py-2 px-3 text-left font-medium text-slate-700">
            <div class="w-[200px] md:max-w-[180px] break-words line-clamp-3">
              {{ el.unit?.course_name?.title || "NA" }}
            </div>
          </td>

          <td class="py-2 px-3 text-left font-medium text-slate-700">
            <div class="w-[200px] md:max-w-[150px] break-words line-clamp-3">
              {{ el.unit?.title || "NA" }}
            </div>
          </td>
          <td class="py-2 px-3 text-left text-gray-600">
            <div class="max-w-[200px] break-words">
              <p :class="isExpanded('Module', index) ? '' : 'line-clamp-2'">
                {{ el.text || "NA" }}
              </p>

              <span
                v-if="el.text && el.text.length > 80"
                @click="toggleRead('Module', index)"
                class="text-blue-600 text-xs cursor-pointer hover:underline"
              >
                {{ isExpanded("Module", index) ? "Read Less" : "Read More" }}
              </span>
            </div>
          </td>

          <td class="py-2 px-3 text-left whitespace-nowrap">
            <div class="flex items-center gap-1.5">
              <div class="flex gap-0.5">
                <div v-for="i in 5" :key="i">
                  <svg
                    :class="['w-3.5 h-3.5', i <= el.rating ? 'text-yellow-400' : 'text-gray-200']"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"/>
                  </svg>
                </div>
              </div>
              <span class="text-[10px] text-gray-400 font-semibold">({{ el.rating }})</span>
            </div>
          </td>

          <td class="py-2 px-3 text-left whitespace-nowrap text-gray-500 font-medium">
            {{ el.created_at ? formatDate(el.created_at) : "NA" }}
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="8" class="text-center py-8 text-gray-600 font-medium">
            No Data Found
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative">
    <div class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal">
      <span class="flex md:hidden">Rows per page:</span>
      <select
        v-model="SessionPageSize"
        @change="onSessionPageSizeChange"
        class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>

    <div v-if="sessionHasLoaded &&SessionTotalPages > 1" class="flex justify-center">
      <Pagination
        :key="'Session-pagination'"
        :totalPages="SessionTotalPages"
        @page-changed="getSession"
      />
    </div>
  </div>
</div>
            <!-- <div
              v-else
              class="py-20 text-center bg-white rounded-[32px] border border-dashed border-slate-300"
            >
              <div class="inline-flex p-4 bg-slate-50 rounded-full mb-4">
                <svg
                  class="w-8 h-8 text-slate-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 class="text-slate-800 font-bold">No Data Found</h3>
            </div> -->
          </div>

          <!-- COURSE TABLE -->
          <div class="w-full space-y-6" v-if="selectedId === 'Course'">
            <div
              class="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between"
            >
              <!-- Left: Title + Search + Filters -->
              <div class="flex-1 space-y-4 w-full">
                <!-- Title + Total Count -->
                <div class="flex flex-col gap-1 mb-2">
                  <h1 class="text-sm font-semibold text-slate-800">Course</h1>
                  <p class="text-xs text-gray-500 font-medium">
                    Total Count :
                    <span class="text-xs font-bold text-blue-600">{{
                      CourseTotalCount
                    }}</span>
                    <span
                      v-if="selectedCourses.length > 0"
                      class="ml-3 pl-3 border-l border-gray-200 text-xs font-medium text-indigo-600"
                    >
                      Selected: {{ selectedCourses.length }}
                    </span>
                  </p>
                </div>

                <!-- Search + Filters -->
                <div class="flex flex-wrap items-center gap-3">
                  <!-- Search Input -->
                  <div class="relative flex-grow max-w-md">
                    <span
                      class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"
                    >
                      <img
                        src="@/assets/search_icon1.svg"
                        class="h-4 w-4 opacity-40"
                        alt="Search"
                      />
                    </span>
                    <input
                      type="text"
                      v-model="CourseSearch"
                      @input="getCourse(1)"
                      placeholder="Search Course title..."
                      class="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-300 rounded-2xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </div>

                  <!-- Course Dropdown -->
                  <div class="w-48">
                    <select
                      v-model="CourseSelectedCourse"
                      @change="
                        fetchAllCourses();
                        getCourse(1);
                        fetchChartCourse();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">All Courses</option>
                      <option
                        v-for="course in courses"
                        :key="course.id"
                        :value="course.id"
                      >
                        {{ course.course_title }}
                      </option>
                    </select>
                  </div>
                  <div class="w-28">
                    <select
                      v-model="selectCoursesRating"
                      @change="
                        getCourse(1);
                        fetchAllCourses();
                      "
                      class="h-10 px-3 py-2 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-700 w-full font-medium focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                    >
                      <option value="">All Ratings</option>
                      <option
                        value="5"
                        class="bg-white text-slate-800 font-normal"
                      >
                        5 ⭐
                      </option>
                      <option
                        value="4"
                        class="bg-white text-slate-800 font-normal"
                      >
                        4 ⭐
                      </option>
                      <option
                        value="3"
                        class="bg-white text-slate-800 font-normal"
                      >
                        3 ⭐
                      </option>
                      <option
                        value="2"
                        class="bg-white text-slate-800 font-normal"
                      >
                        2 ⭐
                      </option>
                      <option
                        value="1"
                        class="bg-white text-slate-800 font-normal"
                      >
                        1 ⭐
                      </option>
                      <option
                        value="0"
                        class="bg-white text-slate-800 font-normal"
                      >
                        0 ★
                      </option>
                    </select>
                  </div>
                  <!-- Clear Button -->
                  <button
                    @click="clearCourseFilters"
                    title="Clear Filters"
                    class="h-10 flex items-center justify-center p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>

                  <!-- Download Button -->
                  <button
                    v-if="feedback_list.length > 0"
                    @click="feedget(selectedId)"
                    class="h-10 flex items-center justify-center gap-2 bg-blues hover:bg-hover text-white rounded-xl py-1.5 px-6 text-sm font-semibold whitespace-nowrap border border-transparent"
                  >
                    <img
                      src="@/assets/newd/download1.png"
                      class="h-4 w-4 brightness-0 invert"
                      alt="Download"
                    />
                    <span>Download</span>
                  </button>
                </div>
              </div>

              <!-- Right: Radial Chart Card -->
              <div class="flex-shrink-0 w-[140px]">
                <div
                  v-if="CourseSelectedCourse"
                  class="flex flex-col items-center bg-slate-50 p-3 rounded-3xl border border-slate-100 min-w-[120px] shadow-sm"
                >
                  <div class="relative w-28 h-24">
                    <ApexChart
                      type="radialBar"
                      :options="getChartCourseOptions('Course')"
                      :series="[CourseChartData.percentage]"
                      height="100%"
                      width="100%"
                    />
                    <!-- Center overlay -->
                    <div
                      class="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <span
                        class="text-lg font-black text-slate-800 leading-none"
                      >
                        {{ CourseChartData.rating }}
                      </span>
                    </div>
                  </div>
                  <div class="space-y-1">
                    <div class="flex gap-0.5">
                      <svg
                        v-for="i in 5"
                        :key="i"
                        :class="
                          i <= CourseChartData.rating
                            ? 'text-amber-400'
                            : 'text-slate-200'
                        "
                        class="w-3 h-3 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                        />
                      </svg>
                    </div>
                  </div>
                  <p
                    class="text-[11px] text-gray-500 font-medium break-words max-w-32 mt-1"
                  >
                    Filter categories by their average rating (out of 5.0).
                  </p>
                </div>
              </div>
            </div>
           <div class="pb-20">
  <div class="w-full rounded-2xl shadow-sm border border-slate-200 overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 text-xs">
      <thead class="bg-gray-50">
        <tr class="divide-x divide-gray-200 whitespace-nowrap">
          <th class="py-2 px-3 font-semibold text-textblues text-left w-12">
            <input
              type="checkbox"
              v-model="selectAllCourse"
              @change="toggleSelectAll('Course')"
              class="w-4 h-4 rounded text-blue-600 border-gray-400"
            />
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-12">
            S.No
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[180px]">
            Student Info
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]">
            Course Title
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[220px]">
            Feedback
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-32">
            Average Ratings
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-24">
            Created At
          </th>
        </tr>
      </thead>

      <tbody v-if="loading" class="divide-y divide-gray-100 bg-white">
        <tr v-for="n in 5" :key="'loader-' + n" class="animate-pulse divide-x divide-gray-50 whitespace-nowrap">
          <td class="py-3 px-3"><div class="h-4 w-4 bg-gray-200 rounded"></div></td>
          <td class="py-3 px-3"><div class="h-3 w-6 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 rounded-full bg-gray-200 flex-shrink-0"></div>
              <div class="flex flex-col gap-1.5 w-full">
                <div class="h-3 w-28 bg-gray-200 rounded"></div>
                <div class="h-2.5 w-36 bg-gray-100 rounded"></div>
              </div>
            </div>
          </td>
          <td class="py-3 px-3"><div class="h-3 w-40 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3">
            <div class="flex flex-col gap-1 w-full">
              <div class="h-3 w-44 bg-gray-100 rounded"></div>
              <div class="h-3 w-28 bg-gray-50 rounded"></div>
            </div>
          </td>
          <td class="py-3 px-3">
            <div class="flex items-center gap-1.5">
              <div class="flex gap-0.5">
                <div v-for="i in 5" :key="i" class="w-3.5 h-3.5 bg-gray-200 rounded-sm"></div>
              </div>
              <div class="h-2.5 w-6 bg-gray-100 rounded"></div>
            </div>
          </td>
          <td class="py-3 px-3"><div class="h-3 w-16 bg-gray-100 rounded"></div></td>
        </tr>
      </tbody>

      <tbody v-else-if="course_list.length > 0" class="divide-y divide-gray-100 bg-white">
        <tr
          v-for="(el, index) in course_list"
          :key="index"
          class="hover:bg-gray-50 transition-colors divide-x divide-gray-100"
        >
          <td class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap">
            <input
              type="checkbox"
              :value="{ Coursefeedback_id: el.id, Courseuser_id: el.user_id_id }"
              v-model="selectedCourses"
              class="w-4 h-4 rounded text-blue-600 border-gray-400"
            />
          </td>
          <td class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap">
            {{ (CourseCurrentPage - 1) * CoursePageSize + index + 1 }}
          </td>
          <td class="py-2 px-3 text-left whitespace-nowrap">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 flex-shrink-0 rounded-full bg-indigo-50 flex items-center justify-center text-[10px] font-bold text-indigo-600">
                {{ el.user_id_display_name?.charAt(0) || "?" }}
              </div>

              <div class="flex flex-col min-w-0">
                <span class="text-xs font-semibold text-slate-700 truncate max-w-[150px]">
                  {{ el.user_id_display_name || "NA" }}
                </span>
                <div class="text-[10px] text-gray-400 flex items-center gap-1 truncate max-w-[150px]">
                  <svg xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="0 -960 960 960" width="10px" fill="#9CA3AF">
                    <path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z"/>
                  </svg>
                  {{ el.user_id_email || "NA" }}
                </div>
              </div>
            </div>
          </td>

          <td class="py-2 px-3 text-left font-medium text-slate-700">
            <div class="w-[200px] md:max-w-[180px] break-words line-clamp-3">
              {{ el.course_id_title || "NA" }}
            </div>
          </td>

          <td class="py-2 px-3 text-left text-gray-600">
            <div class="max-w-[200px] break-words">
              <p :class="isExpanded('Course', index) ? '' : 'line-clamp-2'">
                {{ el.feedback || "NA" }}
              </p>
              <span
                v-if="el.feedback && el.feedback.length > 80"
                @click="toggleRead('Course', index)"
                class="text-blue-600 text-xs cursor-pointer hover:underline"
              >
                {{ isExpanded("Course", index) ? "Read Less" : "Read More" }}
              </span>
            </div>
          </td>

          <td class="py-2 px-3 text-left whitespace-nowrap">
            <div class="flex items-center gap-1.5">
              <div class="flex gap-0.5">
                <div v-for="i in 5" :key="i">
                  <svg
                    :class="['w-3.5 h-3.5', i <= el.rating ? 'text-yellow-400' : 'text-gray-200']"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"/>
                  </svg>
                </div>
              </div>
              <span class="text-[10px] text-gray-400 font-semibold">({{ el.rating }})</span>
            </div>
          </td>

          <td class="py-2 px-3 text-left whitespace-nowrap text-gray-500 font-medium">
            {{ el.created_at ? formatDate(el.created_at) : "NA" }}
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="7" class="text-center py-8 text-gray-600 font-medium">
            No Data Found
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative">
    <div class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal">
      <span class="flex md:hidden">Rows per page:</span>
      <select
        v-model="CoursePageSize"
        @change="onCoursePageSizeChange"
        class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>

    <div v-if="courseHasLoaded && CourseTotalPages > 1" class="flex justify-center">
      <Pagination
        :key="'Course-pagination'"
        :totalPages="CourseTotalPages"
        @page-changed="getCourse"
      />
    </div>
  </div>
</div>
            <!-- <div
              v-else
              class="py-20 text-center bg-white rounded-[32px] border border-dashed border-slate-300"
            >
              <div class="inline-flex p-4 bg-slate-50 rounded-full mb-4">
                <svg
                  class="w-8 h-8 text-slate-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 class="text-slate-800 font-bold">No Data Found</h3>
            </div> -->
          </div>

          <!-- LIVE CLASS TABLE -->
          <div class="w-full space-y-6" v-if="selectedId === 'Live Class'">
            <div
              class="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between"
            >
              <!-- Left: Title + Search + Filters -->
              <div class="flex-1 space-y-4 w-full">
                <!-- Title + Total Count -->
                <div class="flex flex-col gap-1 mb-2">
                  <h1 class="text-sm font-semibold text-slate-800">
                    Live Class :
                  </h1>
                  <p class="text-xs text-gray-500 font-medium">
                    Total Count
                    <span class="text-xs font-bold text-blue-600">{{
                      LiveclassTotalCount
                    }}</span>
                  </p>
                </div>

                <!-- Search + Filters -->
                <div class="flex flex-wrap items-center gap-3">
                  <!-- Search Input -->
                  <div class="relative flex-grow max-w-md">
                    <span
                      class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none"
                    >
                      <img
                        src="@/assets/search_icon1.svg"
                        class="h-4 w-4 opacity-40"
                        alt="Search"
                      />
                    </span>
                    <input
                      type="text"
                      v-model="LiveclassSearch"
                      @input="getLiveclass(1)"
                      placeholder="Search Live Class title..."
                      class="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-300 rounded-2xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </div>

                  <!-- Course Dropdown -->
                  <div class="w-48">
                    <select
                      v-model="LiveclassSelectedCourse"
                      @change="
                        fetchAllLiveclass();
                        getLiveclass(1);
                        fetchLiveclass(LiveclassSelectedCourse);
                        fetchChartLiveclass();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">All Courses</option>
                      <option
                        v-for="course in courses"
                        :key="course.id"
                        :value="course.id"
                      >
                        {{ course.course_title }}
                      </option>
                    </select>
                  </div>

                  <!-- Assignment Dropdown -->
                  <div class="w-48">
                    <select
                      v-model="LiveclassSelectedItem"
                      @change="
                        fetchAllLiveclass();
                        getLiveclass(1);
                        fetchChartLiveclass();
                      "
                      class="h-10 px-4 py-2.5 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-900 w-full focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    >
                      <option value="">Select LiveClass</option>
                      <option
                        v-for="item in Liveclass"
                        :key="item.id"
                        :value="item.id"
                      >
                        {{
                          item.title.length > 40
                            ? item.title.slice(0, 40) + "…"
                            : item.title
                        }}
                      </option>
                    </select>
                  </div>
                  <div class="w-28">
                    <select
                      v-model="selectLiveClassRating"
                      @change="
                        getLiveclass(1);
                        fetchAllLiveclass();
                      "
                      class="h-10 px-3 py-2 bg-slate-50 rounded-2xl text-xs border border-slate-300 text-slate-700 w-full font-medium focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
                    >
                      <option value="">All Ratings</option>
                      <option
                        value="5"
                        class="bg-white text-slate-800 font-normal"
                      >
                        5 ⭐
                      </option>
                      <option
                        value="4"
                        class="bg-white text-slate-800 font-normal"
                      >
                        4 ⭐
                      </option>
                      <option
                        value="3"
                        class="bg-white text-slate-800 font-normal"
                      >
                        3 ⭐
                      </option>
                      <option
                        value="2"
                        class="bg-white text-slate-800 font-normal"
                      >
                        2 ⭐
                      </option>
                      <option
                        value="1"
                        class="bg-white text-slate-800 font-normal"
                      >
                        1 ⭐
                      </option>
                      <option
                        value="0"
                        class="bg-white text-slate-800 font-normal"
                      >
                        0 ★
                      </option>
                    </select>
                  </div>
                  <!-- Clear Button -->
                  <button
                    @click="clearLiveclassFilters"
                    title="Clear Filters"
                    class="h-10 flex items-center justify-center p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>

                  <!-- Download Button -->
                  <button
                    v-if="feedback_list.length > 0"
                    @click="feedget(selectedId)"
                    class="h-10 flex items-center justify-center gap-2 bg-blues hover:bg-hover text-white rounded-xl py-1.5 px-6 text-sm font-semibold whitespace-nowrap border border-transparent"
                  >
                    <img
                      src="@/assets/newd/download1.png"
                      class="h-4 w-4 brightness-0 invert"
                      alt="Download"
                    />
                    <span>Download</span>
                  </button>
                </div>
              </div>

              <!-- Right: Radial Chart Card -->
              <div class="flex-shrink-0 w-[140px]">
                <div
                  v-if="LiveclassSelectedItem"
                  class="flex flex-col items-center bg-slate-50 p-3 rounded-3xl border border-slate-100 min-w-[120px] shadow-sm"
                >
                  <div class="relative w-28 h-24">
                    <ApexChart
                      type="radialBar"
                      :options="getChartLiveclassOptions('Live Class')"
                      :series="[LiveclassChartData.percentage]"
                      height="100%"
                      width="100%"
                    />
                    <!-- Center overlay -->
                    <div
                      class="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <span
                        class="text-lg font-black text-slate-800 leading-none"
                      >
                        {{ LiveclassChartData.rating }}
                      </span>
                    </div>
                  </div>
                  <div class="space-y-1">
                    <div class="flex gap-0.5">
                      <svg
                        v-for="i in 5"
                        :key="i"
                        :class="
                          i <= LiveclassChartData.rating
                            ? 'text-amber-400'
                            : 'text-slate-200'
                        "
                        class="w-3 h-3 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path
                          d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                        />
                      </svg>
                    </div>
                  </div>
                  <p
                    class="text-[11px] text-gray-500 font-medium break-words max-w-32 mt-1"
                  >
                    Filter categories by their average rating (out of 5.0).
                  </p>
                </div>
              </div>
            </div>
            <div class="pb-20">
  <div class="w-full rounded-2xl shadow-sm border border-slate-200 overflow-x-auto">
    <table class="min-w-full divide-y divide-gray-200 text-xs">
      <thead class="bg-gray-50">
        <tr class="divide-x divide-gray-200 whitespace-nowrap">
          <th class="py-2 px-3 font-semibold text-textblues text-left w-12">
            <input
              type="checkbox"
              v-model="selectAllLiveClass"
              @change="toggleSelectAll('Live Class')"
              class="w-4 h-4 rounded text-blue-600 border-gray-400"
            />
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-12">
            S.No
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[180px]">
            Student Info
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]">
            Course Title
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[200px]">
            Liveclass Title
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left min-w-[220px]">
            Feedback
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-32">
            Average Ratings
          </th>
          <th scope="col" class="py-2 px-3 font-semibold text-textblues text-left w-24">
            Created At
          </th>
        </tr>
      </thead>

      <tbody v-if="loading" class="divide-y divide-gray-100 bg-white">
        <tr v-for="n in 5" :key="'loader-' + n" class="animate-pulse divide-x divide-gray-50 whitespace-nowrap">
          <td class="py-3 px-3"><div class="h-4 w-4 bg-gray-200 rounded"></div></td>
          <td class="py-3 px-3"><div class="h-3 w-6 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 rounded-full bg-gray-200 flex-shrink-0"></div>
              <div class="flex flex-col gap-1.5 w-full">
                <div class="h-3 w-28 bg-gray-200 rounded"></div>
                <div class="h-2.5 w-36 bg-gray-100 rounded"></div>
              </div>
            </div>
          </td>
          <td class="py-3 px-3"><div class="h-3 w-40 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3"><div class="h-3 w-36 bg-gray-100 rounded"></div></td>
          <td class="py-3 px-3">
            <div class="flex flex-col gap-1 w-full">
              <div class="h-3 w-44 bg-gray-100 rounded"></div>
              <div class="h-3 w-28 bg-gray-50 rounded"></div>
            </div>
          </td>
          <td class="py-3 px-3">
            <div class="flex items-center gap-1.5">
              <div class="flex gap-0.5">
                <div v-for="i in 5" :key="i" class="w-3.5 h-3.5 bg-gray-200 rounded-sm"></div>
              </div>
              <div class="h-2.5 w-6 bg-gray-100 rounded"></div>
            </div>
          </td>
          <td class="py-3 px-3"><div class="h-3 w-16 bg-gray-100 rounded"></div></td>
        </tr>
      </tbody>

      <tbody v-else-if="Liveclass_list.length > 0" class="divide-y divide-gray-100 bg-white">
        <tr
          v-for="(el, index) in Liveclass_list"
          :key="index"
          class="hover:bg-gray-50 transition-colors divide-x divide-gray-100"
        >
          <td class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap">
            <input
              type="checkbox"
              :value="{ Liveclassfeedback_id: el.id, Liveclassuser_id: el.user_id?.id }"
              v-model="selectedLiveClasses"
              class="w-4 h-4 rounded text-blue-600 border-gray-400"
            />
          </td>
          <td class="py-2 px-3 text-left text-gray-500 font-medium whitespace-nowrap">
            {{ (LiveclassCurrentPage - 1) * LiveclassPageSize + index + 1 }}
          </td>
          <td class="py-2 px-3 text-left whitespace-nowrap">
            <div class="flex items-center gap-2">
              <div class="w-7 h-7 flex-shrink-0 rounded-full bg-indigo-50 flex items-center justify-center text-[10px] font-bold text-indigo-600">
                {{ el.user_id?.display_name?.charAt(0) || "?" }}
              </div>

              <div class="flex flex-col min-w-0">
                <span class="text-xs font-semibold text-slate-700 truncate max-w-[150px]">
                  {{ el.user_id?.display_name || "NA" }}
                </span>
                <div class="text-[10px] text-gray-400 flex items-center gap-1 truncate max-w-[150px]">
                  <svg xmlns="http://www.w3.org/2000/svg" height="10px" viewBox="0 -960 960 960" width="10px" fill="#9CA3AF">
                    <path d="M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h640q33 0 56.5 23.5T880-720v480q0 33-23.5 56.5T800-160H160Zm320-280L160-640v400h640v-400L480-440Zm0-80 320-200H160l320 200ZM160-640v-80 480-400Z"/>
                  </svg>
                  {{ el.user_id?.email || "NA" }}
                </div>
              </div>
            </div>
          </td>

          <td class="py-2 px-3 text-left font-medium text-slate-700">
            <div class="w-[200px] md:max-w-[180px] break-words line-clamp-3">
              {{ el.live_class?.course_title || "NA" }}
            </div>
          </td>
          <td class="py-2 px-3 text-left font-medium text-slate-700">
            <div class="w-[200px] md:max-w-[150px] break-words line-clamp-3">
              {{ el.live_class?.title || "NA" }}
            </div>
          </td>

          <td class="py-2 px-3 text-left text-gray-600">
            <div class="max-w-[200px] break-words">
              <p :class="isExpanded('Live Class', index) ? '' : 'line-clamp-2'">
                {{ el.text || "NA" }}
              </p>
              <span
                v-if="el.text && el.text.length > 80"
                @click="toggleRead('Live Class', index)"
                class="text-blue-600 text-xs cursor-pointer hover:underline"
              >
                {{ isExpanded("Live Class", index) ? "Read Less" : "Read More" }}
              </span>
            </div>
          </td>

          <td class="py-2 px-3 text-left whitespace-nowrap">
            <div class="flex items-center gap-1.5">
              <div class="flex gap-0.5">
                <div v-for="i in 5" :key="i">
                  <svg
                    :class="['w-3.5 h-3.5', i <= el.rating ? 'text-yellow-400' : 'text-gray-200']"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"/>
                  </svg>
                </div>
              </div>
              <span class="text-[10px] text-gray-400 font-semibold">({{ el.rating }})</span>
            </div>
          </td>

          <td class="py-2 px-3 text-left whitespace-nowrap text-gray-500 font-medium">
            {{ el.created_at ? formatDate(el.created_at) : "NA" }}
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="8" class="text-center py-8 text-gray-600 font-medium">
            No Data Found
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="flex flex-col sm:flex-row justify-center gap-3 mt-4 relative">
    <div class="md:absolute md:left-3 md:top-4 gap-2 text-sm text-gray-600 flex justify-center items-center md:flex-none md:justify-normal">
      <span class="flex md:hidden">Rows per page:</span>
      <select
        v-model="LiveclassPageSize"
        @change="onCLiveclassPageSizeChange"
        class="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>

    <div v-if="liveclassHasLoaded &&LiveclassTotalPages > 1" class="flex justify-center">
      <Pagination
        :key="'Liveclass-pagination'"
        :totalPages="LiveclassTotalPages"
        ref="paginationRef"
        @page-changed="getLiveclass"
      />
    </div>
  </div>
</div>
            <!-- <div
              v-else
              class="py-20 text-center bg-white rounded-[32px] border border-dashed border-slate-300"
            >
              <div class="inline-flex p-4 bg-slate-50 rounded-full mb-4">
                <svg
                  class="w-8 h-8 text-slate-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 class="text-slate-800 font-bold">No Data Found</h3>
            </div> -->
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </div>
</template>

<script>
import Header from "@/components/Admin/Header.vue";
import FlashMessage from "@/components/FlashMessage.vue";
import axios from "axios";
import Pagination from "@/components/Pagination.vue";
import VueApexCharts from "vue3-apexcharts";
import Footer from "@/components/Footer.vue";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      messages: { text: "", type: "null" },
      disable_submit: false,
      loading: true,
    assignmentHasLoaded: false,
    assessmentHasLoaded: false,
    lessonHasLoaded: false,
    sessionHasLoaded: false,
    courseHasLoaded: false,
    liveclassHasLoaded: false,
      loadingCharts: true,

      // Assignments pagination
      assignmentPageSize: 10,
      assignmentCurrentPage: 1,
      assignmentTotalPages: 1,
      assignmentTotalCount: 0,
      assignment_list: [],
      assignmentSearch: "",
      assignmentSelectedCourse: "",
      assignmentSelectedItem: "",
      assignmentChartData: { rating: 0, percentage: 0 },
      selectAssignRating: "",

      // Assessments pagination
      assessmentPageSize: 10,
      assessmentCurrentPage: 1,
      assessmentTotalPages: 1,
      assessmentTotalCount: 0,
      assessment_list: [],
      assessmentSearch: "",
      assessmentSelectedCourse: "",
      assessmentSelectedItem: "",
      assessmentChartData: { rating: 0, percentage: 0 },
      selectAssessRating: "",

      // Lesson pagination
      lessonPageSize: 10,
      lessonCurrentPage: 1,
      lessonTotalPages: 1,
      lessonTotalCount: 0,
      lesson_list: [],
      lessonSearch: "",
      lessonSelectedCourse: "",
      lessonSelectedItem: "",
      lessonChartData: { rating: 0, percentage: 0 },
      selectUnitRating: "",

      // Session pagination
      SessionPageSize: 10,
      SessionCurrentPage: 1,
      SessionTotalPages: 1,
      SessionTotalCount: 0,
      Session_list: [],
      SessionSearch: "",
      SessionSelectedCourse: "",
      SessionSelectedItem: "",
      SessionChartData: { rating: 0, percentage: 0 },
      selectModuleRating: "",

      // Liveclass pagination
      LiveclassPageSize: 10,
      LiveclassCurrentPage: 1,
      LiveclassTotalPages: 1,
      LiveclassTotalCount: 0,
      Liveclass_list: [],
      LiveclassSearch: "",
      LiveclassSelectedCourse: "",
      LiveclassSelectedItem: "",
      LiveclassChartData: { rating: 0, percentage: 0 },
      selectLiveClassRating: "",

      // Course pagination
      CoursePageSize: 10,
      CourseCurrentPage: 1,
      CourseTotalPages: 1,
      CourseTotalCount: 0,
      course_list: [],
      CourseSearch: "",
      CourseSelectedCourse: "",
      CourseChartData: { rating: 0, percentage: 0 },
      selectCoursesRating: "",

      // Common data
      feedback_list: [],
      selectedId: "",
      response_message: "",
      courses: [],
      Assignments: [],
      Assessments: [],
      Lessons: [],
      Sessions: [],
      Liveclass: [],
      // Session_list: [],
      // course_list: [],
      // Liveclass_list: [],
      seriesData: [],
      selectedChartLabel: "Over ALL",
      chartData: { rating: 0, percentage: 0 },
      expandedRows: [],

      selectAllAssignment: false,
      selectedAssignments: [],
      Allassignment: [],

      selectAllAssessment: false,
      selectedAssessments: [],
      Allassessment: [],

      selectAllUnit: false,
      selectedUnit: [],
      Allunit: [],

      selectAllModule: false,
      selectedModule: [],
      Allmodule: [],

      selectAllCourse: false,
      selectedCourses: [],
      Allcourses: [],

      selectAllLiveClass: false,
      selectedLiveClasses: [],
      Allliveclass: [],
    };
  },
  components: {
    Header,
    FlashMessage,
    Pagination,
    Footer,
    ApexChart: VueApexCharts,
  },
  computed: {
    ...mapGetters(["userInfo"]),
    AssignfeedbackIds() {
      return this.selectedAssignments.map((item) => item.Assignfeedback_id);
    },

    AssignuserIds() {
      return this.selectedAssignments.map((item) => item.Assignuser_id);
    },
    AssessfeedbackIds() {
      return this.selectedAssessments.map((item) => item.Assessfeedback_id);
    },

    AssessuserIds() {
      return this.selectedAssessments.map((item) => item.Assessuser_id);
    },
    UnitfeedbackIds() {
      return this.selectedUnit.map((item) => item.Unitfeedback_id);
    },

    UnituserIds() {
      return this.selectedUnit.map((item) => item.Unituser_id);
    },
    ModulefeedbackIds() {
      return this.selectedModule.map((item) => item.Modulefeedback_id);
    },

    ModuleuserIds() {
      return this.selectedModule.map((item) => item.Moduleuser_id);
    },
    CoursefeedbackIds() {
      return this.selectedCourses.map((item) => item.Coursefeedback_id);
    },

    CourseuserIds() {
      return this.selectedCourses.map((item) => item.Courseuser_id);
    },
    LiveclassfeedbackIds() {
      return this.selectedLiveClasses.map((item) => item.Liveclassfeedback_id);
    },

    LiveclassuserIds() {
      return this.selectedLiveClasses.map((item) => item.Liveclassuser_id);
    },
  },
  watch: {
    selectedId(newVal) {
      this.selectedChartLabel = newVal;
      this.fetchSchema();
    },
    selectedAssignments(val) {
      this.selectAllAssignment =
        val.length > 0 && val.length === this.Allassignment.length;
    },

    selectedAssessments(val) {
      this.selectAllAssessment =
        val.length > 0 && val.length === this.Allassessment.length;
    },
    selectedUnit(val) {
      this.selectAllUnit = val.length > 0 && val.length === this.Allunit.length;
    },
    selectedModule(val) {
      this.selectAllModule =
        val.length > 0 && val.length === this.Allmodule.length;
    },

    selectedCourses(val) {
      this.selectAllCourse =
        val.length > 0 && val.length === this.Allcourses.length;
    },

    selectedLiveClasses(val) {
      this.selectAllLiveClass =
        val.length > 0 && val.length === this.Allliveclass.length;
    },
  },
  created() {
    this.fetchChart();
    this.getfeedback();
    this.fetchCourse();
    this.fetchAllAssignment();
    this.fetchAllAssessment();
    this.fetchAllUnit();
    this.fetchAllModule();
    this.fetchAllCourses();
    this.fetchAllLiveclass();
  },
  methods: {
    toggleRead(type, index) {
      const key = `${type}-${index}`;

      if (this.expandedRows.includes(key)) {
        this.expandedRows = this.expandedRows.filter((item) => item !== key);
      } else {
        this.expandedRows.push(key);
      }
    },

    isExpanded(type, index) {
      return this.expandedRows.includes(`${type}-${index}`);
    },

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");
      const seconds = String(d.getSeconds()).padStart(2, "0");
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12 || 12;
      hours = String(hours).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}, ${hours}:${minutes}:${seconds} ${ampm}`;
    },

    async fetchCourse() {
      await axios
        .get(
          `${process.env.VUE_APP_API}/courses/title/?Key=${this.userInfo.token_key}&is_published=true`,
        )
        .then((response) => {
          if (response.data) this.courses = response.data.data;
        });
    },

    async fetchAssignment(id) {
      await axios
        .get(
          `${process.env.VUE_APP_API}/Inst_Assignment/view/?course_id=${id}&Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data) this.Assignments = response.data.data.data;
        });
    },

    async fetchAssessments(id) {
      await axios
        .get(
          `${process.env.VUE_APP_API}/coursequiz/name/list/?course=${id}&Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data) this.Assessments = response.data;
        });
    },

    async fetchLesson(id) {
      await axios
        .get(
          `${process.env.VUE_APP_API}/lessons/?course_name=${id}&Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data) this.Lessons = response.data.results;
        });
    },

    async fetchSession(id) {
      await axios
        .get(
          `${process.env.VUE_APP_API}/section/?course_name=${id}&Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data) this.Sessions = response.data.data;
        });
    },

    async fetchLiveclass(id) {
      await axios
        .get(
          `${process.env.VUE_APP_API}/calendar/view/?course_data__id=${id}&Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data) this.Liveclass = response.data.data;
        });
    },

    async getfeedback() {
      await axios
        .get(
          `${process.env.VUE_APP_API}/feedback-titles/?Key=${this.userInfo.token_key}`,
        )
        .then((response) => {
          if (response.data.status == 200) {
            this.feedback_list = response.data.data;
            if (this.feedback_list.length > 0) {
              this.selectedId = this.feedback_list[0];
              this.fetchSchema();
            }
          }
        });
    },

    fetchSchema() {
      this.assignmentSearch = "";
      this.assignmentSelectedCourse = "";
      this.assignmentSelectedItem = "";

      this.assessmentSearch = "";
      this.assessmentSelectedCourse = "";
      this.assessmentSelectedItem = "";
      this.lessonSearch = "";
      this.lessonSelectedCourse = "";
      this.lessonSelectedItem = "";
      this.SessionSearch = "";
      this.SessionSelectedCourse = "";
      this.SessionSelectedItem = "";
      this.LiveclassSearch = "";
      this.LiveclassSelectedCourse = "";
      this.LiveclassSelectedItem = "";
      this.CourseSearch = "";
      this.CourseSelectedCourse = "";
      this.assessment_list = [];
      this.lesson_list = [];
      this.Session_list = [];
      this.course_list = [];
      this.Liveclass_list = [];
      this.expandedRows = [];
      this.selectAssignRating = "";
      this.selectAssessRating = "";
      this.selectUnitRating = "";
      this.selectModuleRating = "";
      this.selectLiveClassRating = "";
      this.selectCoursesRating = "";

      switch (this.selectedId) {
        case "Assignments":
             this.assignmentHasLoaded = false;
      this.loading = true;
          this.getAssignment(1);
          break;
        case "Assessments":
            this.assessmentHasLoaded = false;
      this.loading = true;
          this.getAssessments(1);
          break;
        case "Unit":
             this.lessonHasLoaded = false;
      this.loading = true;
          this.getLesson(1);
          break;
        case "Module":
             this.sessionHasLoaded = false;
      this.loading = true;
          this.getSession(1);
          break;
        case "Course":
             this.courseHasLoaded = false;
      this.loading = true;
          this.getCourse(1);
          break;
        case "Live Class":
             this.liveclassHasLoaded = false;
      this.loading = true;
          this.getLiveclass(1);
          break;
        default:
          break;
      }
    },
    async fetchAllAssignment() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/assignment-feedback-list-without-pagination/?Key=${this.userInfo.token_key}&course_id__in=${this.assignmentSelectedCourse}&assignment_id__in=${this.assignmentSelectedItem}&search=${this.assignmentSearch}&rating=${this.selectAssignRating}`,
        );
        if (response.data.status === 200) {
          this.Allassignment = response.data.data;
          this.assignmentTotalCount = response.data.count;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async fetchAllAssessment() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/quiz-feedback-list-without-pagination/?Key=${this.userInfo.token_key}&course_id__in=${this.assessmentSelectedCourse}&quize_id__in=${this.assessmentSelectedItem}&search=${this.assessmentSearch}&rating=${this.selectAssessRating}`,
        );
        if (response.data.status === 200) {
          this.Allassessment = response.data.data;
          this.assessmentTotalCount = response.data.count;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async fetchAllUnit() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/lesson-feedback-list-without-pagination/?Key=${this.userInfo.token_key}&course_id__in=${this.lessonSelectedCourse}&lesson_id__in=${this.lessonSelectedItem}&search=${this.lessonSearch}&rating=${this.selectUnitRating}`,
        );
        if (response.data.status === 200) {
          this.Allunit = response.data.data;
          this.lessonTotalCount = response.data.count;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async fetchAllModule() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/unit-feedback-list-without-pagination/?Key=${this.userInfo.token_key}&course_id__in=${this.SessionSelectedCourse}&unit_id__in=${this.SessionSelectedItem}&search=${this.SessionSearch}&rating=${this.selectModuleRating}`,
        );
        if (response.data.status === 200) {
          this.Allmodule = response.data.data;
          this.SessionTotalCount = response.data.count;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async fetchAllCourses() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/course-feedback-list-without-pagination/?Key=${this.userInfo.token_key}&course_id__in=${this.CourseSelectedCourse}&search=${this.CourseSearch}&rating=${this.selectCoursesRating}`,
        );
        if (response.data.status === 200) {
          this.Allcourses = response.data.data;
          this.CourseTotalCount = response.data.count;
        }
      } catch (err) {
        console.error(err);
      }
    },
    async fetchAllLiveclass() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/live-class-feedback-list-without-pagination/?Key=${this.userInfo.token_key}&course_id__in=${this.LiveclassSelectedCourse}&live_class__id=${this.LiveclassSelectedItem}&search=${this.LiveclassSearch}&rating=${this.selectLiveClassRating}`,
        );
        if (response.data.status === 200) {
          this.Allliveclass = response.data.data;
          this.LiveclassTotalCount = response.data.count;
        }
      } catch (err) {
        console.error(err);
      }
    },

    toggleSelectAll(type) {
      if (type === "Assignments") {
        this.selectedAssignments = this.selectAllAssignment
          ? this.Allassignment.map((x) => ({
              Assignfeedback_id: x.id,
              Assignuser_id: x.user_id.id,
            }))
          : [];
      }

      if (type === "Assessments") {
        this.selectedAssessments = this.selectAllAssessment
          ? this.Allassessment.map((x) => ({
              Assessfeedback_id: x.id,
              Assessuser_id: x.user_id.id,
            }))
          : [];
      }
      if (type === "Unit") {
        this.selectedUnit = this.selectAllUnit
          ? this.Allunit.map((x) => ({
              Unitfeedback_id: x.id,
              Unituser_id: x.user_id.id,
            }))
          : [];
      }
      if (type === "Module") {
        this.selectedModule = this.selectAllModule
          ? this.Allmodule.map((x) => ({
              Modulefeedback_id: x.id,
              Moduleuser_id: x.user_id.id,
            }))
          : [];
      }

      if (type === "Course") {
        this.selectedCourses = this.selectAllCourse
          ? this.Allcourses.map((x) => ({
              Coursefeedback_id: x.id,
              Courseuser_id: x.user_id_id,
            }))
          : [];
      }

      if (type === "Live Class") {
        this.selectedLiveClasses = this.selectAllLiveClass
          ? this.Allliveclass.map((x) => ({
              Liveclassfeedback_id: x.id,
              Liveclassuser_id: x.user_id.id,
            }))
          : [];
      }
    },

    // ASSIGNMENT METHODS
    async getAssignment(page) {
      this.assignmentCurrentPage = page;
      let params = {
        Key: this.userInfo.token_key,
        page: page,
        page_size: this.assignmentPageSize,
      };
      if (this.assignmentSelectedCourse)
        params.course_id__in = this.assignmentSelectedCourse;
      if (this.assignmentSelectedItem)
        params.assignment_id__in = this.assignmentSelectedItem;
      if (this.assignmentSearch) params.search = this.assignmentSearch;
      if (this.selectAssignRating) {
        params.rating = this.selectAssignRating;
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/assignment-feedback/list/`,
          { params },
        );
        if (response.data.data.status === 200) {
          this.assignment_list = response.data.data.data;
          this.assignmentTotalPages = response.data.total_page;
          this.assignmentCurrentPage = response.data.current_page;
          this.assignmentTotalCount = response.data.total;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.assignmentHasLoaded = true;
      }
    },

    onAssignmentPageSizeChange() {
      this.getAssignment(1);
    },

    clearAssignmentFilters() {
      this.assignmentSearch = "";
      this.assignmentSelectedCourse = "";
      this.assignmentSelectedItem = "";
      this.assignmentChartData = { rating: 0, percentage: 0 };
      this.selectAssignRating = "";
      this.selectedAssignments = [];
      this.getAssignment(1);
    },

    // ASSESSMENT METHODS
    async getAssessments(page) {
      this.assessmentCurrentPage = page;
      // this.loading = true;
      let params = {
        Key: this.userInfo.token_key,
        page: page,
        page_size: this.assessmentPageSize,
      };
      if (this.assessmentSelectedCourse)
        params.course_id__in = this.assessmentSelectedCourse;
      if (this.assessmentSelectedItem)
        params.quize_id__in = this.assessmentSelectedItem;
      if (this.assessmentSearch) params.search = this.assessmentSearch;
      if (this.selectAssessRating) params.rating = this.selectAssessRating;

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/quiz-feedback/list/`,
          { params },
        );
        if (response.data.data.status === 200) {
          this.assessment_list = response.data.data.data;
          this.assessmentTotalPages = response.data.total_page;
          this.assessmentCurrentPage = response.data.current_page;
          this.assessmentTotalCount = response.data.total;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.assessmentHasLoaded = true;
      }
    },

    onAssessmentPageSizeChange() {
      this.getAssessments(1);
    },

    clearAssessmentFilters() {
      this.assessmentSearch = "";
      this.assessmentSelectedCourse = "";
      this.assessmentSelectedItem = "";
      this.assessmentChartData = { rating: 0, percentage: 0 };
      this.selectAssessRating = "";
      this.selectedAssessments = [];
      this.getAssessments(1);
    },

    // LESSON METHODS
    async getLesson(page) {
      this.lessonCurrentPage = page;
      let params = {
        Key: this.userInfo.token_key,
        page: page,
        page_size: this.lessonPageSize,
      };
      if (this.lessonSelectedCourse)
        params.course_id__in = this.lessonSelectedCourse;
      if (this.lessonSelectedItem)
        params.lesson_id__in = this.lessonSelectedItem;
      if (this.lessonSearch) params.search = this.lessonSearch;
      if (this.selectUnitRating) params.rating = this.selectUnitRating;

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/lesson-feedback/list/`,
          { params },
        );
        if (response.data) {
          this.lesson_list = response.data.data;
          this.lessonTotalPages = response.data.total_page;
          this.lessonCurrentPage = response.data.current_page;
          this.lessonTotalCount = response.data.total;
        }
      } catch (error) {
        console.error(error);
      }finally {
        this.loading = false;
        this.lessonHasLoaded = true;
      }
    },

    onLessonPageSizeChange() {
      this.getLesson(1);
    },

    clearLessonFilters() {
      this.lessonSearch = "";
      this.lessonSelectedCourse = "";
      this.lessonSelectedItem = "";
      this.lessonChartData = { rating: 0, percentage: 0 };
      this.selectUnitRating = "";
      this.selectedUnit = [];
      this.getLesson(1);
    },

    async getSession(page) {
      this.SessionCurrentPage = page;

      let params = {
        Key: this.userInfo.token_key,
        page: page,
        page_size: this.SessionPageSize,
      };

      if (this.SessionSelectedCourse) {
        params.course_id__in = this.SessionSelectedCourse;
      }
      if (this.SessionSelectedItem) {
        params.unit_id__in = this.SessionSelectedItem;
      }
      if (this.SessionSearch) {
        params.search = this.SessionSearch;
      }
      if (this.selectModuleRating) {
        params.rating = this.selectModuleRating;
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/unit-feedback/list/`,
          { params },
        );

        if (response.data) {
          this.Session_list = response.data.data;
          this.SessionTotalPages = response.data.total_page;
          this.SessionCurrentPage = response.data.current_page;
          this.SessionTotalCount = response.data.total;
        }
      } catch (error) {
        console.error(error);
      }finally {
        this.loading = false;
        this.sessionHasLoaded = true;
      }
    },
    onSessionPageSizeChange() {
      this.getSession(1);
    },

    clearSessionFilters() {
      this.SessionSearch = "";
      this.SessionSelectedCourse = "";
      this.SessionSelectedItem = "";
      this.SessionChartData = { rating: 0, percentage: 0 };
      this.selectModuleRating = "";
      this.selectedModule = [];
      this.getSession(1);
    },

    async getCourse(page) {
      this.CourseCurrentPage = page;

      let params = {
        Key: this.userInfo.token_key,
        page: page,
        page_size: this.CoursePageSize,
      };

      if (this.CourseSelectedCourse) {
        params.course_id__in = this.CourseSelectedCourse;
      }
      if (this.CourseSearch) {
        params.search = this.CourseSearch;
      }
      if (this.selectCoursesRating) {
        params.rating = this.selectCoursesRating;
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/course-feedback-get/`,
          { params },
        );

        if (response.data) {
          this.course_list = response.data.data;
          this.CourseTotalPages = response.data.total_page;
          this.CourseCurrentPage = response.data.current_page;
          this.CourseTotalCount = response.data.total;
        }
      } catch (error) {
        console.error(error);
      }finally {
        this.loading = false;
        this.courseHasLoaded = true;
      }
    },
    onCoursePageSizeChange() {
      this.getCourse(1);
    },

    clearCourseFilters() {
      this.CourseSearch = "";
      this.CourseSelectedCourse = "";
      this.CourseChartData = { rating: 0, percentage: 0 };
      this.selectCoursesRating = "";
      this.selectedCourses = [];
      this.getCourse(1);
    },
    async getLiveclass(page) {
      this.currentPage = page;

      let params = {
        Key: this.userInfo.token_key,
        page: page,
        page_size: this.LiveclassPageSize,
      };

      if (this.LiveclassSelectedCourse) {
        params.course_id__in = this.LiveclassSelectedCourse;
      }
      if (this.LiveclassSelectedItem) {
        params.live_class__id = this.LiveclassSelectedItem;
      }
      if (this.LiveclassSearch) {
        params.search = this.LiveclassSearch;
      }
      if (this.selectLiveClassRating) {
        params.rating = this.selectLiveClassRating;
      }

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/live_class-feedback/list/`,
          { params },
        );

        if (response.data) {
          this.Liveclass_list = response.data.data;
          this.LiveclassTotalPages = response.data.total_page;
          this.LiveclassCurrentPage = response.data.current_page;
          this.LiveclassTotalCount = response.data.total;
        }
      } catch (error) {
        console.error(error);
      }finally {
        this.loading = false;
        this.liveclassHasLoaded = true;
      }
    },
    onCLiveclassPageSizeChange() {
      this.getLiveclass(1);
    },

    clearLiveclassFilters() {
      this.LiveclassSearch = "";
      this.LiveclassSelectedCourse = "";
      this.LiveclassSelectedItem = "";
      this.LiveclassChartData = { rating: 0, percentage: 0 };
      this.selectLiveClassRating = "";
      this.selectedLiveClasses = [];
      this.getLiveclass(1);
    },
    async feedget(type) {
      let params = {
        Key: this.userInfo.token_key,
        data: type,
      };

      if (type === "Assignments") {
        if (
          this.selectedAssignments.length > 0 ||
          this.assignmentSelectedCourse ||
          this.assignmentSelectedItem ||
          this.selectAssignRating !== ""
        ) {
          return await this.exportAssignmentFeedback();
        }
        params.assignments = this.selectedAssignment;
      }
      if (type === "Assessments") {
        if (
          this.selectedAssessments.length > 0 ||
          this.assessmentSelectedCourse ||
          this.assessmentSelectedItem ||
          this.selectAssessRating !== ""
        ) {
          return await this.exportAssessmentFeedback();
        }
        params.assessments = this.selectedAssessments;
      }
      if (type === "Unit") {
        if (
          this.selectedUnit.length > 0 ||
          this.lessonSelectedCourse ||
          this.lessonSelectedItem ||
          this.selectUnitRating !== ""
        ) {
          return await this.exportUnitFeedback();
        }
        params.lessons = this.selectedLesson;
      }
      if (type === "Module") {
        if (
          this.selectedModule.length > 0 ||
          this.SessionSelectedCourse ||
          this.SessionSelectedItem ||
          this.selectModuleRating !== ""
        ) {
          return await this.exportModuleFeedback();
        }
        params.units = this.selectedSession;
      }
      if (type === "Course") {
        if (
          this.selectedCourses.length > 0 ||
          this.CourseSelectedCourse ||
          this.selectCoursesRating !== ""
        ) {
          return await this.exportCourseFeedback();
        }
        params.courses = this.selectedCourses;
      }
      if (type === "Live Class") {
        if (
          this.selectedLiveClasses.length > 0 ||
          this.LiveclassSelectedCourse ||
          this.LiveclassSelectedItem ||
          this.selectLiveClassRating !== ""
        ) {
          return await this.exportLiveclassFeedback();
        }
        params.live_class = this.selectedCourses;
      }
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/overall-feedback/download/`,
          { params },
        );

        if (response) {
          this.response_message =
            "Your file is ready to download go to download history page";

          setTimeout(() => {
            this.response_message = "";
          }, 5000);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async exportAssignmentFeedback() {
      try {
        const payload = {
          user_ids:
            this.selectedAssignments.length > 0 ? this.AssignuserIds : [],

          course_id: this.assignmentSelectedCourse || null,

          assignment_id: this.assignmentSelectedItem || null,

          rating:
            this.selectAssignRating !== ""
              ? Number(this.selectAssignRating)
              : null,

          assignment_feedback_ids:
            this.selectedAssignments.length > 0 ? this.AssignfeedbackIds : [],
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API}/assignment-feedback/export/?Key=${this.userInfo.token_key}`,
          payload,
          {
            headers: {
              Authorization: `Token ${this.userInfo.token_key}`,
            },
          },
        );

        if (response.status === 200 || response.status === 201) {
          this.response_message =
            "Your file is ready to download. Go to download history page.";

          setTimeout(() => {
            this.response_message = "";
            this.selectedAssignments = [];
            this.assignmentSelectedCourse = "";
            this.assignmentSelectedItem = "";
            this.selectAssignRating = "";
            this.fetchAllAssignment();
            this.getAssignment(1);
          }, 5000);
        }
      } catch (error) {
        console.error(error);

        this.response_message =
          error.response?.data?.message || "Export failed.";

        setTimeout(() => {
          this.response_message = "";
        }, 5000);
      }
    },
    async exportAssessmentFeedback() {
      try {
        const payload = {
          user_ids:
            this.selectedAssessments.length > 0 ? this.AssessuserIds : [],

          course_id: this.assessmentSelectedCourse || null,

          coursequiz_id: this.assessmentSelectedItem || null,

          rating:
            this.selectAssessRating !== ""
              ? Number(this.selectAssessRating)
              : null,

          quiz_feedback_ids:
            this.selectedAssessments.length > 0 ? this.AssessfeedbackIds : [],
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API}/quiz-feedback/export/?Key=${this.userInfo.token_key}`,
          payload,
          {
            headers: {
              Authorization: `Token ${this.userInfo.token_key}`,
            },
          },
        );

        if (response.status === 200 || response.status === 201) {
          this.response_message =
            "Your file is ready to download. Go to download history page.";

          setTimeout(() => {
            this.response_message = "";
            this.selectedAssessments = [];
            this.assessmentSelectedCourse = "";
            this.assessmentSelectedItem = "";
            this.selectAssessRating = "";
            this.fetchAllAssessment();
            this.getAssessments(1);
          }, 5000);
        }
      } catch (error) {
        console.error(error);

        this.response_message =
          error.response?.data?.message || "Export failed.";

        setTimeout(() => {
          this.response_message = "";
        }, 5000);
      }
    },
    async exportUnitFeedback() {
      try {
        const payload = {
          user_ids: this.selectedUnit.length > 0 ? this.UnituserIds : [],

          course_id: this.lessonSelectedCourse || null,

          lesson_id: this.lessonSelectedItem || null,

          rating:
            this.selectUnitRating !== "" ? Number(this.selectUnitRating) : null,

          lesson_feedback_ids:
            this.selectedUnit.length > 0 ? this.UnitfeedbackIds : [],
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API}/lesson-feedback/export/?Key=${this.userInfo.token_key}`,
          payload,
          {
            headers: {
              Authorization: `Token ${this.userInfo.token_key}`,
            },
          },
        );

        if (response.status === 200 || response.status === 201) {
          this.response_message =
            "Your file is ready to download. Go to download history page.";

          setTimeout(() => {
            this.response_message = "";
            this.selectedUnit = [];
            this.lessonSelectedCourse = "";
            this.lessonSelectedItem = "";
            this.selectUnitRating = "";
            this.fetchAllUnit();
            this.getLesson(1);
          }, 5000);
        }
      } catch (error) {
        console.error(error);

        this.response_message =
          error.response?.data?.message || "Export failed.";

        setTimeout(() => {
          this.response_message = "";
        }, 5000);
      }
    },
    async exportModuleFeedback() {
      try {
        const payload = {
          user_ids: this.selectedModule.length > 0 ? this.ModuleuserIds : [],

          course_id: this.SessionSelectedCourse || null,

          section_id: this.SessionSelectedItem || null,

          rating:
            this.selectModuleRating !== ""
              ? Number(this.selectModuleRating)
              : null,

          unit_feedback_ids:
            this.selectedModule.length > 0 ? this.ModulefeedbackIds : [],
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API}/unit-feedback/export/?Key=${this.userInfo.token_key}`,
          payload,
          {
            headers: {
              Authorization: `Token ${this.userInfo.token_key}`,
            },
          },
        );

        if (response.status === 200 || response.status === 201) {
          this.response_message =
            "Your file is ready to download. Go to download history page.";

          setTimeout(() => {
            this.response_message = "";
            this.selectedModule = [];
            this.SessionSelectedCourse = "";
            this.SessionSelectedItem = "";
            this.selectModuleRating = "";
            this.fetchAllModule();
            this.getSession(1);
          }, 5000);
        }
      } catch (error) {
        console.error(error);

        this.response_message =
          error.response?.data?.message || "Export failed.";

        setTimeout(() => {
          this.response_message = "";
        }, 5000);
      }
    },
    async exportCourseFeedback() {
      try {
        const payload = {
          user_ids: this.selectedCourses.length > 0 ? this.CourseuserIds : [],

          course_id: this.CourseSelectedCourse || null,

          rating:
            this.selectCoursesRating !== ""
              ? Number(this.selectCoursesRating)
              : null,

          course_feedback_ids:
            this.selectedCourses.length > 0 ? this.CoursefeedbackIds : [],
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API}/course-feedback/export/?Key=${this.userInfo.token_key}`,
          payload,
          {
            headers: {
              Authorization: `Token ${this.userInfo.token_key}`,
            },
          },
        );

        if (response.status === 200 || response.status === 201) {
          this.response_message =
            "Your file is ready to download. Go to download history page.";

          setTimeout(() => {
            this.response_message = "";
            this.selectedCourses = [];
            this.CourseSelectedCourse = "";
            this.selectCoursesRating = "";
            this.fetchAllCourses();
            this.getCourse(1);
          }, 5000);
        }
      } catch (error) {
        console.error(error);

        this.response_message =
          error.response?.data?.message || "Export failed.";

        setTimeout(() => {
          this.response_message = "";
        }, 5000);
      }
    },
    async exportLiveclassFeedback() {
      try {
        const payload = {
          user_ids:
            this.selectedLiveClasses.length > 0 ? this.LiveclassuserIds : [],

          course_id: this.LiveclassSelectedCourse || null,

          calendar_id: this.LiveclassSelectedItem || null,
          rating:
            this.selectLiveClassRating !== ""
              ? Number(this.selectLiveClassRating)
              : null,

          live_class_feedback_ids:
            this.selectedLiveClasses.length > 0
              ? this.LiveclassfeedbackIds
              : [],
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API}/live-class-feedback/export/?Key=${this.userInfo.token_key}`,
          payload,
          {
            headers: {
              Authorization: `Token ${this.userInfo.token_key}`,
            },
          },
        );

        if (response.status === 200 || response.status === 201) {
          this.response_message =
            "Your file is ready to download. Go to download history page.";

          setTimeout(() => {
            this.response_message = "";
            this.selectedLiveClasses = [];
            this.LiveclassSelectedCourse = "";
            this.LiveclassSelectedItem = "";
            this.selectLiveClassRating = "";
            this.fetchAllLiveclass();
            this.getLiveclass(1);
          }, 5000);
        }
      } catch (error) {
        console.error(error);

        this.response_message =
          error.response?.data?.message || "Export failed.";

        setTimeout(() => {
          this.response_message = "";
        }, 5000);
      }
    },
    selectChart(label) {
      this.selectedChartLabel = label;
      const labelToIdMap = {
        "Over ALL": "",
        Assignments: "Assignments",
        Assessments: "Assessments",
        Unit: "Unit",
        Module: "Module",
        "Live Class": "Live Class",
        Course: "Course",
      };
      this.selectedId = labelToIdMap[label] || "";
      this.assignmentChartData = { rating: 0, percentage: 0 };
      this.assessmentChartData = { rating: 0, percentage: 0 };
      this.lessonChartData = { rating: 0, percentage: 0 };
      this.SessionChartData = { rating: 0, percentage: 0 };
      this.LiveclassChartData = { rating: 0, percentage: 0 };
      this.CourseChartData = { rating: 0, percentage: 0 };

      this.fetchSchema();
    },

    async fetchChart() {
      this.loadingCharts = true;
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/feedback/average-rating/?Key=${this.userInfo.token_key}`,
        );
        if (response.data.status == 200) {
          const data = response.data.data;
          this.seriesData = [
            { label: "Over ALL", rating: data.overall_avg_rating },
            { label: "Assignments", rating: data.assignment_avg_rating },
            { label: "Assessments", rating: data.quiz_avg_rating },
            { label: "Unit", rating: data.lesson_avg_rating },
            { label: "Module", rating: data.unit_avg_rating },
            { label: "Live Class", rating: data.live_class_avg_rating },
            { label: "Course", rating: data.course_avg_rating },
          ].map((item) => ({ ...item, percentage: item.rating * 20 }));
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingCharts = false;
      }
    },

    getChartOptions(label) {
      return {
        chart: { type: "radialBar", sparkline: { enabled: true } },
        colors: ["#2563EB"],
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            hollow: { size: "65%" },
            track: { background: "#E5E7EB", strokeWidth: "97%", margin: 5 },
            dataLabels: { show: false },
          },
        },
        fill: {
          type: "gradient",
          gradient: {
            shade: "dark",
            type: "horizontal",
            gradientToColors: ["#7C3AED"],
            stops: [0, 100],
          },
        },
        stroke: { lineCap: "round" },
        labels: [label],
      };
    },

    async fetchChartAssignment() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/assignment-feedback-average/?course_id=${this.assignmentSelectedCourse}&assignment_id=${this.assignmentSelectedItem}&Key=${this.userInfo.token_key}`,
        );
        if (response.data.status === 200) {
          const data = response.data.data;
          this.assignmentChartData.rating = data.avg_rating || 0;
          this.assignmentChartData.percentage = data.avg_rating * 20;
          this.assignmentTotalCount = data.total_feedback;
        }
      } catch (err) {
        console.error(err);
      }
    },

    getChartAssignOptions(label) {
      return this.getChartOptions(label);
    },

    async fetchChartAssessment() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/quiz-feedback-average/?course_id=${this.assessmentSelectedCourse}&quiz_id=${this.assessmentSelectedItem}&Key=${this.userInfo.token_key}`,
        );
        if (response.data.status === 200) {
          const data = response.data.data;
          this.assessmentChartData.rating = data.avg_rating || 0;
          this.assessmentChartData.percentage = data.avg_rating * 20;
          this.assessmentTotalCount = data.total_feedback;
        }
      } catch (err) {
        console.error(err);
      }
    },

    getChartAssessOptions(label) {
      return this.getChartOptions(label);
    },

    async fetchChartLesson() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/lesson-feedback-average/?course_id=${this.lessonSelectedCourse}&lesson_id=${this.lessonSelectedItem}&Key=${this.userInfo.token_key}`,
        );
        if (response.data.status === 200) {
          const data = response.data.data;
          this.lessonChartData.rating = data.avg_rating || 0;
          this.lessonChartData.percentage = data.avg_rating * 20;
          this.lessonTotalCount = data.total_feedback;
        }
      } catch (err) {
        console.error(err);
      }
    },

    getChartLessonOptions(label) {
      return this.getChartOptions(label);
    },

    async fetchChartSession() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/unit-feedback-average/?course_id=${this.SessionSelectedCourse}&section_id=${this.SessionSelectedItem}&Key=${this.userInfo.token_key}`,
        );
        if (response.data.status === 200) {
          const data = response.data.data;
          this.SessionChartData.rating = data.avg_rating || 0;
          this.SessionChartData.percentage = data.avg_rating * 20;
          this.SessionTotalCount = data.total_feedback;
        }
      } catch (err) {
        console.error(err);
      }
    },

    getChartSessionOptions(label) {
      return this.getChartOptions(label);
    },

    async fetchChartLiveclass() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/live-class-feedback-average/?course_id=${this.LiveclassSelectedCourse}&calendar_id=${this.LiveclassSelectedItem}&Key=${this.userInfo.token_key}`,
        );
        if (response.data.status === 200) {
          const data = response.data.data;
          this.LiveclassChartData.rating = data.avg_rating || 0;
          this.LiveclassChartData.percentage = data.avg_rating * 20;
          this.LiveclassTotalCount = data.total_feedback;
        }
      } catch (err) {
        console.error(err);
      }
    },
    getChartLiveclassOptions(label) {
      return this.getChartOptions(label);
    },

    async fetchChartCourse() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API}/course/${this.CourseSelectedCourse}/average-feedback/?Key=${this.userInfo.token_key}`,
        );
        if (response.data.status === 200) {
          const data = response.data.data;
          this.CourseChartData.rating = data.average_rating || 0;
          this.CourseChartData.percentage = data.average_rating * 20;
          this.CourseTotalCount = data.total_feedback;
        }
      } catch (err) {
        console.error(err);
      }
    },

    getChartCourseOptions(label) {
      return this.getChartOptions(label);
    },
  },
};
</script>

<style scoped>
.heightfix {
  height: calc(100vh - 170px) !important;
}
</style>
